// ============================================================
// Central Game State Manager (singleton) — v2.0
// ============================================================

import type { PlayerState, BattleState, Equipment, EquipmentSlot, Hero, HeroAbility, BaseUpgrade, PlayerFaction, Quest, HeroClass, TomeId, ConsumableId, MerchantItem, MerchantEquipmentItem, MerchantMysteryItem, ItemRarity, EnemyFaction, AchievementProgress, AccountData, StoryQuest } from '@roe/shared';
import { createDefaultStoryQuests, advanceStoryQuests, type StoryQuestEvent, type StoryQuestAdvanceResult } from '@roe/shared';
import { AuthService } from '../services/AuthService.js';
import {
  createHero,
  addXp,
  levelHeroTo,
  BASE_STARTING_HP,
  BASE_HP_REGEN,
  BASE_STARTING_HERO_SLOTS,
  UPGRADE_COST_EXPONENT,
  UPGRADE_BASE_COSTS,
  ARCHER_MAX_ARROWS,
  ASSASSIN_MAX_ENERGY,
  BASE_STARTING_ATTACK,
  BASE_STARTING_ATTACK_SPEED,
  HERO_CLASS_NAMES,
  HERO_CHARACTER_NAMES,
  GOLD_REVIVE_COST_PER_LEVEL,
  WEAPON_CLASS_RESTRICTIONS,
  ARMOR_CLASS_RESTRICTIONS,
  TWO_HANDED_WEAPON_TYPES,
  createDefaultTomes,
  pickTome,
  getTomeBonus,
  TOME_DEFINITIONS,
  applyTalentPoint,
  getAvailableTalentPoints,
  getTalentUpgradeInfo,
  CONSUMABLE_DEFS,
  MERCHANT_REFRESH_INTERVAL,
  MERCHANT_INVENTORY_SIZE,
  WAVES_PER_ZONE,
  generateEquipment,
  generateEquipmentForType,
  repairArmorSpriteKey,
  repairWeaponSpriteKey,
  RARITY_SELL_MULT,
  ACHIEVEMENTS,
  createDefaultLifetimeStats,
  createDefaultAccountData,
  getChainTiers,
  getEffectiveStats,
  CLASS_BASE_STATS,
  CLASS_STAT_GROWTH,
  getHeroAbilities,
  getPassiveBonuses,
  computeLegacyRefund,
  generateSigil,
  generateSpecificSigil,
  ARMORY_MAIN_XP_SHARE,
  ARMORY_RECRUIT_XP_SHARE,
  ARMORY_MAX_LEVEL,
  ARMORY_MAX_PASSIVES,
  ARMORY_RESPEC_COST_RUNES,
  armoryXpToNext,
  armoryTotalXpForLevel,
  resolveArmoryLevel,
  unlockedPassiveSlots,
  emptyArmoryHeroState,
  getPassiveDef,
  getPassivesForHero,
  rollDailyQuests,
  rollWeeklyQuests,
  shouldRollDaily,
  shouldRollWeekly,
  lastDailyResetBefore,
  lastWeeklyResetBefore,
  getQuestPoolEntry,
  MASTERY_CHALLENGES,
  getMasteryChallenge,
} from '@roe/shared';
import type { MasteryChallenge } from '@roe/shared';
import type { ArmoryHeroState, PassiveDef, Sigil, SigilSpecial, HeroLifetimeStats, HeroQuestProgress } from '@roe/shared';

export interface LeaderboardEntry {
  name: string;
  score: number;
  level: number;
  wave: number;
  /** Main hero used for the run — heroKey when the player picked a unique
   *  character override (e.g. 'amazon', 'sorcerer', 'necromancer'), else
   *  heroClass ('warrior', 'tank', …). Optional — old rows have null. */
  heroKey?: string | null;
  /** Tomes (Books) picked during the run — comma-separated TomeId list,
   *  ordered by tome level desc. Optional — old rows have null. */
  tomeIds?: string | null;
  date: string;
}

export interface InfernoLeaderboardEntry {
  name: string;
  score: number;
  /** Inferno zone reached (1-based) */
  infernoZone: number;
  wave: number;
  /** Same as LeaderboardEntry.heroKey — see above. */
  heroKey?: string | null;
  /** Same as LeaderboardEntry.tomeIds — see above. */
  tomeIds?: string | null;
  date: string;
}

/** Maps heroKey overrides to their canonical character names (heroClass fallback used otherwise) */
const HERO_KEY_NAMES: Record<string, string> = { dwarf: 'Lootfi', amazon: 'Zayla', necromancer: 'Morveth', valkyrie: 'Valka', sorcerer: 'Cyrith' };
const resolveHeroName = (heroClass: HeroClass, heroKey?: string, override?: string): string =>
  override ?? (heroKey ? (HERO_KEY_NAMES[heroKey] ?? HERO_CHARACTER_NAMES[heroClass]) : HERO_CHARACTER_NAMES[heroClass]);

function getApiBase(): string {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.hostname !== 'localhost') return '';
  return 'http://localhost:4000';
}

export async function postLeaderboardToServer(
  entry: { mode: 'standard' | 'inferno'; name: string; score: number; level?: number; infernoZone?: number; wave: number; heroKey?: string | null; tomeIds?: string | null; date: string },
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    await fetch(`${getApiBase()}/api/leaderboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(entry),
    });
  } catch { /* offline — ignore */ }
}

function createDefaultBaseUpgrades(): BaseUpgrade[] {
  return [
    {
      id: 'base_hp', name: 'Fortify Walls', description: '+100 Base HP per level',
      category: 'fortification', currentLevel: 0, maxLevel: 50,
      cost: { gold: 100, loot: 20 }, effect: 'base_hp', effectValue: 100,
    },
    {
      id: 'base_regen', name: 'Repair Crews', description: '+0.5 HP regen/sec per level',
      category: 'fortification', currentLevel: 0, maxLevel: 20,
      cost: { gold: 150, loot: 30 }, effect: 'base_regen', effectValue: 0.5,
    },
    {
      id: 'hero_slots', name: 'Expand Barracks', description: '+1 hero slot per level',
      category: 'barracks', currentLevel: 0, maxLevel: 5,
      cost: { gold: 500, loot: 100 }, effect: 'hero_slots', effectValue: 1,
    },
    {
      id: 'xp_bonus', name: 'Training Grounds', description: '+10% XP gain per level',
      category: 'barracks', currentLevel: 0, maxLevel: 20,
      cost: { gold: 200, loot: 40 }, effect: 'xp_bonus', effectValue: 0.10,
    },
    {
      id: 'loot_bonus', name: 'Lucky Charm', description: '+5% loot drop rate per level',
      category: 'armory', currentLevel: 0, maxLevel: 20,
      cost: { gold: 250, loot: 50 }, effect: 'loot_bonus', effectValue: 0.05,
    },
    {
      id: 'gold_bonus', name: 'Gold Mine', description: '+8% gold gain per level',
      category: 'resource', currentLevel: 0, maxLevel: 25,
      cost: { gold: 120, loot: 25 }, effect: 'gold_bonus', effectValue: 0.08,
    },
    {
      id: 'base_attack', name: 'Arrow Tower', description: '+3 base arrow damage per level',
      category: 'fortification', currentLevel: 0, maxLevel: 30,
      cost: { gold: 200, loot: 40 }, effect: 'base_attack', effectValue: 3,
    },
    {
      id: 'base_attack_speed', name: 'Rapid Fire', description: '-100ms base attack speed per level',
      category: 'fortification', currentLevel: 0, maxLevel: 20,
      cost: { gold: 300, loot: 60 }, effect: 'base_attack_speed', effectValue: 100,
    },
  ];
}

/** Default quests available from the start.
 *  Seeds with a fresh roll of 3 dailies + 3 weeklies — `maybeRollResets()`
 *  will then refresh them on UTC midnight rollover. The legacy hard-coded
 *  list (kill_50 / kill_200 / faction_orc / etc.) is gone; quests now come
 *  exclusively from the `DAILY_QUEST_POOL` / `WEEKLY_QUEST_POOL` defined in
 *  `@roe/shared`. */
function createDefaultQuests(): Quest[] {
  return [...rollDailyQuests(), ...rollWeeklyQuests()];
}

function createNewPlayer(slot = 0): PlayerState {
  return {
    id: 'player_1',
    name: 'Player',
    faction: 'alliance',
    setupComplete: false,
    heroes: [],
    deployedHeroes: [],
    inventory: [],
    resources: { gold: 50, loot: 0 },
    base: {
      hp: BASE_STARTING_HP,
      maxHp: BASE_STARTING_HP,
      hpRegen: BASE_HP_REGEN,
      heroSlots: BASE_STARTING_HERO_SLOTS,
      maxHeroSlots: 6,
      upgrades: createDefaultBaseUpgrades(),
      baseAttack: BASE_STARTING_ATTACK,
      baseAttackSpeed: BASE_STARTING_ATTACK_SPEED,
    },
    currentLevel: 1,
    highestLevel: 1,
    totalPlayTime: 0,
    totalKills: 0,
    highScore: 0,
    autoRun: false,
    quests: createDefaultQuests(),
    completedQuestIds: [],
    lastDailyReset: lastDailyResetBefore(Date.now()),
    lastWeeklyReset: lastWeeklyResetBefore(Date.now()),
    dailyQuestsClaimedAllTime: 0,
    weeklyDailyClaimedSnapshot: 0,
    storyQuests: createDefaultStoryQuests(),
    newLootCount: 0,
    saveSlot: slot,
    tomes: createDefaultTomes(),
    chosenTomeTypes: [],
    stayCount: 0,
    activeConsumables: [],
    merchant: { items: [], equipmentItems: [], lastRefreshWave: 0 },
    trackedQuests: [],
    sigilBag: [],
    sigilsEquipped: [],
    sigilSlots: 1,
  };
}

export class GameStateManager {
  private static instance: GameStateManager;
  /** Called after every in-memory save when logged in — wired to SyncService.syncSave for server persistence.
   *  Third param `immediate` bypasses the debounce timer for critical saves (stage transitions, death). */
  static onSaveCallback: ((slot: number, data: unknown, immediate?: boolean) => void) | null = null;
  public player: PlayerState;
  public battle: BattleState;
  /** Account-wide data (achievements, lifetime stats) — shared across all save slots */
  public account: AccountData;
  /** Runtime-only flag — not saved to localStorage. Toggled by /god cheat command. */
  public godMode = false;
  /** In-memory save slot cache — populated by init(), updated by save() */
  private saveCache: Map<number, PlayerState> = new Map();
  /** In-memory run session cache — updated by saveRunSession(), cleared by clearRunSession().
   *  runGold / runLoot / runLootSpent / runXp / runItems are the run-scoped counters
   *  used by the end-of-run summary; persisted so Ctrl+Shift+R doesn't zero them. */
  private sessionCache: Map<number, {
    level: number; waveNumber: number; runScore: number;
    infernoMode: boolean; infernoZone: number; infernoScore: number;
    waveJustCompleted?: boolean;
    runGold?: number; runLoot?: number; runLootSpent?: number; runXp?: number; runItems?: number;
  }> = new Map();
  /** True after init() has completed — BattleScene guards on this before entering gameplay */
  public isInitialized = false;
  /** True only after the account row was successfully READ back from the server
   *  (or confirmed-absent via a 200-OK with `account: null`). Stays false on
   *  network failure / fetch throw / 5xx. `_pushAccountToServer` refuses to PUT
   *  while this is false so a transient init-time fetch failure never
   *  overwrites valid server-side account data (armory levels, passive tree,
   *  tokens) with the empty in-memory default. Reset to false on logout +
   *  every init() call so each session has to re-confirm freshness. */
  private accountFetchedFromServer = false;
  /** Debounce timer for account server sync (independent from save slot timer) */
  private accountSyncTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly ACCOUNT_DEBOUNCE_MS = 10_000;

  static getInstance(): GameStateManager {
    if (!GameStateManager.instance) GameStateManager.instance = new GameStateManager();
    return GameStateManager.instance;
  }

  static _resetInstance(): void { (GameStateManager as any).instance = undefined; }

  /** Wipe in-memory game state on logout so the next user doesn't inherit the previous user's
   *  account data, saves, or run session. Fresh account + fresh player slot 0. */
  resetForLogout(): void {
    if (this.accountSyncTimer !== null) {
      clearTimeout(this.accountSyncTimer);
      this.accountSyncTimer = null;
    }
    this.saveCache.clear();
    this.sessionCache.clear();
    this.account = createDefaultAccountData();
    this.player = createNewPlayer(0);
    this.isInitialized = false;
    this.accountFetchedFromServer = false;
  }

  private constructor() {
    this.account = createDefaultAccountData();
    this.player = createNewPlayer(0);
    this.saveCache = new Map();
    this.isInitialized = false;
    this.migratePlayer();

    this.battle = {
      enemies: [],
      waveNumber: 0,
      totalWaves: 10,
      isRunning: false,
      bossDefeated: false,
      pendingLoot: { gold: 0, xp: 0, items: [], loot: 0, spells: [] },
    };
  }

  private migratePlayer(): void {
    const p = this.player;
    // Migrate old saves missing fields
    if (p.faction === undefined) p.faction = 'alliance';
    if (p.setupComplete === undefined) p.setupComplete = true;
    if (p.autoRun === undefined) p.autoRun = false;
    if (p.quests === undefined) p.quests = createDefaultQuests();
    if (p.completedQuestIds === undefined) p.completedQuestIds = [];
    // ── Quest pool migration ─────────────────────────────────────────
    // Pre-pool saves carry the legacy hard-coded quest ids
    // (`daily_kill_50` / `faction_orc` / `weekly_kill_200` / etc.).
    // Drop any quest whose id isn't in the current pool — `maybeRollResets`
    // on next scene enter will fill the daily/weekly slots back in.
    p.quests = p.quests.filter((q) => getQuestPoolEntry(q.id) !== undefined);
    if (p.lastDailyReset === undefined) p.lastDailyReset = 0;        // forces a daily roll on next maybeRollResets
    if (p.lastWeeklyReset === undefined) p.lastWeeklyReset = 0;       // same for weekly
    if (p.dailyQuestsClaimedAllTime === undefined) p.dailyQuestsClaimedAllTime = 0;
    if (p.weeklyDailyClaimedSnapshot === undefined) p.weeklyDailyClaimedSnapshot = 0;
    // Story quests — seed + merge new quest chains into old saves without resetting progress.
    // Also forward-merge new fields (e.g. requiresQuestId) onto pre-existing saved quests so
    // gating added in later patches takes effect retroactively.
    if (p.storyQuests === undefined) p.storyQuests = createDefaultStoryQuests();
    const existingStoryIds = new Set(p.storyQuests.map((q) => q.id));
    for (const def of createDefaultStoryQuests()) {
      if (!existingStoryIds.has(def.id)) {
        p.storyQuests.push(def);
        continue;
      }
      const saved = p.storyQuests.find((q) => q.id === def.id)!;
      // Forward-merge new metadata fields, and ALSO reconcile removed ones — if the current
      // def no longer has a requiresQuestId, drop it from the saved quest so the chain gate
      // doesn't keep applying. (Archive Beneath moved from chained → independent in the
      // "archivist never spawned" fix.)
      if (saved.requiresQuestId === undefined && def.requiresQuestId !== undefined) {
        saved.requiresQuestId = def.requiresQuestId;
      } else if (saved.requiresQuestId !== undefined && def.requiresQuestId === undefined) {
        saved.requiresQuestId = undefined;
      }
      if (saved.zone !== def.zone) saved.zone = def.zone;
      if (saved.minStage !== def.minStage) saved.minStage = def.minStage;
      // Unstick enter_zone beats that were left with progress=0.0001 from the pre-fix
      // behavior (beat was considered "started" but never marked done, so currentBeat
      // stayed at 0 forever). If the active beat is an enter_zone trigger AND progress
      // was already bumped, promote it to done + advance currentBeat so the next beat
      // activates without requiring the player to re-enter the zone.
      for (let bi = 0; bi <= saved.currentBeat && bi < saved.beats.length; bi++) {
        const b = saved.beats[bi];
        if (b.trigger.kind === 'enter_zone' && !b.done && b.progress > 0) {
          b.done = true;
          b.progress = 1;
          if (bi === saved.currentBeat) saved.currentBeat++;
        }
      }
    }
    // Relic slot 3 / 4 quest gate — legacy migration. Slot 3 ↔ shadow_veil, slot 4 ↔
    // voidelf_rite. The check is gated on the actual presence of an EQUIPPED item in
    // the slot (proof the player had the slot unlocked under the previous version).
    // Previously this also auto-claimed voidelf_rite when labyrinths_heart was
    // claimed (legacy mapping where labyrinths_heart gated slot 4) — that branch
    // leaked free slot-4 access to every new player who finished the labyrinth
    // quest, since labyrinths_heart is mid-game and voidelf_rite is end-game.
    // Removed; legacy users who completed labyrinths_heart under the OLD mapping
    // but never picked up a relic for slot 4 just need to clear voidelf_rite once.
    const anyRelic3 = p.heroes.some((h) => h.equipment.relic3 !== null);
    const anyRelic4 = p.heroes.some((h) => h.equipment.relic4 !== null);
    if (anyRelic3) {
      const sv = p.storyQuests.find((q) => q.id === 'shadow_veil');
      if (sv && !sv.claimed) sv.claimed = true;
    }
    if (anyRelic4) {
      const ve = p.storyQuests.find((q) => q.id === 'voidelf_rite');
      if (ve && !ve.claimed) ve.claimed = true;
    }
    // Mirror claimed story quests both ways so unlocks are account-wide. Pushes every
    // cached save's claimed quests into the account list, then pulls the full account
    // list back into the active slot. Running across every cached save (not just the
    // active one) is load-bearing: a player who completed "Veil of the Shadow Realm"
    // on slot 2 and then spins up a fresh slot 1 needs slot 2's claim mirrored to the
    // account list before slot 1's migrate can inherit it.
    if (!this.account.claimedStoryQuestIds) this.account.claimedStoryQuestIds = [];
    const harvestClaims = (quests: StoryQuest[] | undefined): void => {
      if (!quests) return;
      for (const q of quests) {
        if (q.claimed && !this.account.claimedStoryQuestIds!.includes(q.id)) {
          this.account.claimedStoryQuestIds!.push(q.id);
        }
      }
    };
    harvestClaims(p.storyQuests);
    for (const cached of this.saveCache.values()) harvestClaims(cached?.storyQuests);
    for (const id of this.account.claimedStoryQuestIds) {
      const q = p.storyQuests.find((s) => s.id === id);
      if (q && !q.claimed) q.claimed = true;
    }
    // One-time catch-up for `reach_stage` beats that the player already passed
    // BEFORE the prereq quest was claimed. The advance_stage event fires only
    // on stage transitions, so a newly-unlocked quest never sees the crossed
    // threshold and its beat stays stuck at 0/1 (user-reported Eternal Pyre
    // bug: "can't complete from whatever save"). Synthesize an advance_stage
    // event at the player's CURRENT level so any unlocked-but-stuck reach_stage
    // beats catch up immediately on load.
    if (typeof p.currentLevel === 'number' && p.currentLevel > 0) {
      advanceStoryQuests(p.storyQuests, {
        kind: 'advance_stage',
        newStage: p.currentLevel,
        stage: p.currentLevel,
        stayCount: p.stayCount ?? 0,
      });
    }
    // De-dupe unique relics: pre-fix saves could equip the same-name unique relic into
    // two slots on the same hero (fresh item ids per drop meant the prior equip code had
    // nothing to catch the duplication). Walk each hero's relic slots and, for any unique
    // name that appears more than once, keep the lowest-indexed slot's copy and push the
    // others back to inventory so the player still owns them and can re-equip elsewhere.
    for (const h of p.heroes) {
      const seenUnique = new Set<string>();
      for (const rSlot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
        const r = h.equipment[rSlot];
        if (!r || r.rarity !== 'unique') continue;
        if (seenUnique.has(r.name)) {
          if (!p.inventory.some((i) => i.id === r.id)) p.inventory.push(r);
          (h.equipment as Record<string, Equipment | null>)[rSlot] = null;
        } else {
          seenUnique.add(r.name);
        }
      }
    }
    if (p.newLootCount === undefined) p.newLootCount = 0;
    // Hero Armory — account-level progression per heroKey. Existing saves
    // start at 0 across the board; the field materialises lazily on first grant.
    // Hero Armory is account-wide (moved from PlayerState to AccountData). Existing
    // saves that landed armoryByHero on the player need a one-time migration: merge
    // the slot's map into the account map, picking the higher-level entry per heroKey
    // so no progress is lost when multiple slots had their own copies.
    if (this.account.armoryByHero === undefined) this.account.armoryByHero = {};
    if ((p as unknown as { armoryByHero?: Record<string, ArmoryHeroState> }).armoryByHero) {
      const slotMap = (p as unknown as { armoryByHero: Record<string, ArmoryHeroState> }).armoryByHero;
      for (const [k, v] of Object.entries(slotMap)) {
        const existing = this.account.armoryByHero[k];
        if (!existing || (v.level ?? 1) > (existing.level ?? 1) || (v.xp ?? 0) > (existing.xp ?? 0)) {
          this.account.armoryByHero[k] = { ...v, passiveIds: [...(v.passiveIds ?? [])] };
        }
      }
      delete (p as unknown as { armoryByHero?: unknown }).armoryByHero;
    }
    // Sigil system (phase 2). Preserve existing data on old saves.
    if (p.sigilBag === undefined) p.sigilBag = [];
    if (p.sigilsEquipped === undefined) p.sigilsEquipped = [];
    // Base sigil slot count is always 1 — extra slots come from the passive tree.
    // Older saves shipped sigilSlots=3 by accident; force-reset so tree progression is honoured.
    p.sigilSlots = 1;
    // Trim equipped overflow if tree nodes were reset: keep only the first N where N = effective slots.
    {
      const treeBonus = Math.floor(getPassiveBonuses(p.passiveTree ?? {}).sigilSlots);
      const effSlots = Math.min(3, 1 + treeBonus);
      if ((p.sigilsEquipped?.length ?? 0) > effSlots) {
        p.sigilsEquipped = p.sigilsEquipped!.slice(0, effSlots);
      }
    }
    if (!p.resources) p.resources = { gold: 0, loot: 0 } as any;
    // Migrate roeTokens → loot (belt-and-suspenders; server does this too on GET /api/resources)
    if ((p.resources as any).roeTokens) {
      p.resources.loot = (p.resources.loot ?? 0) + (p.resources as any).roeTokens;
      delete (p.resources as any).roeTokens;
    }
    // Migrate gems → loot (gems currency removed; server does this too on GET /api/resources)
    if ((p.resources as any).gems) {
      p.resources.loot = (p.resources.loot ?? 0) + (p.resources as any).gems;
      delete (p.resources as any).gems;
    }
    // Migrate materials → loot rename; also fix NaN from the rename transition
    if (p.resources.loot === undefined || isNaN(p.resources.loot as number)) {
      p.resources.loot = (p.resources as any).materials ?? 0;
    }
    // Defensive NaN / undefined guard on gold — a stale save with a missing field,
    // or a prior NaN produced by `+= undefined` math, would otherwise propagate through
    // every future gold-gain operation and display as "NaN" in the HUD.
    if (p.resources.gold === undefined || p.resources.gold === null || isNaN(p.resources.gold as number)) {
      p.resources.gold = 0;
    }

    // ── v2 Talent-tree migration ─────────────────────────────────────────
    // The Ancestral Rune Tree was redesigned from a 4-path circle into
    // 3 branches (WRATH / WARD / CUNNING). Refund every rune the player
    // had invested in the v1 tree so they can re-spec freely under v2.
    // Runs once per account (gated by `account.passiveTreeMigratedV2`).
    if (!this.account.passiveTreeMigratedV2) {
      const v1Tree = this.account.passiveTree ?? p.passiveTree ?? {};
      const refund = computeLegacyRefund(v1Tree);
      if (refund > 0) {
        // Refund into account.tokens (canonical store) and the live player.resources.loot.
        this.account.tokens = (this.account.tokens ?? 0) + refund;
        p.resources.loot = (p.resources.loot ?? 0) + refund;
      }
      this.account.passiveTree = {};
      p.passiveTree = {};
      this.account.passiveTreeMigratedV2 = true;
    }

    if (p.saveSlot === undefined) p.saveSlot = 0;
    if (p.base.baseAttack === undefined) p.base.baseAttack = BASE_STARTING_ATTACK;
    if (p.base.baseAttackSpeed === undefined) p.base.baseAttackSpeed = BASE_STARTING_ATTACK_SPEED;
    if ((p as any).stayCount === undefined) p.stayCount = 0;
    if (p.infernoRunActive === undefined) p.infernoRunActive = false;
    if (!p.lockedRelics) p.lockedRelics = [];

    for (const hero of p.heroes) {
      // Migrate old saves where Lootfi was stored as heroClass:'dwarf' (now heroClass:'warrior', heroKey:'dwarf')
      if ((hero.heroClass as string) === 'dwarf') {
        hero.heroClass = 'warrior';
        if (!hero.heroKey) hero.heroKey = 'dwarf';
      }
      // Fix Zayla saved with 'Sylvari' name (heroKey='amazon' but name inherited from archer class)
      if (hero.heroKey === 'amazon' && hero.name === 'Sylvari') hero.name = 'Zayla';
      // Fix Lootfi saved with 'Goomar' name
      if (hero.heroKey === 'dwarf' && hero.name === 'Goomar') hero.name = 'Lootfi';
      // Valkas created before `valkyrie` was added to HERO_KEY_NAMES inherited
      // the warrior-class default name. Rename them on load so the info panel
      // stops calling her Goomar.
      if (hero.heroKey === 'valkyrie' && hero.name === 'Goomar') hero.name = 'Valka';
      // Same fix for Cyrith — created before `sorcerer` was added to
      // HERO_KEY_NAMES, so wizards with heroKey 'sorcerer' inherited the
      // wizard-class default 'Zyphara'. Rename on load.
      if (hero.heroKey === 'sorcerer' && hero.name === 'Zyphara') hero.name = 'Cyrith';
      // Cyrith stale-ability migration — saves created when 'sorcerer' was
      // first added used the wizard-class spell list (Fireball / Eruption)
      // instead of Cyrith's (Forge Vorrak / Storm Bolt / Static Field /
      // Arc Strike). The result was a Cyrith hero throwing orange Fireball
      // explosions instead of cyan storm bolts (user-reported VFX bug).
      // Detect any Zyphara-era spell id and rebuild the abilities list
      // from the canonical sorcerer spell list. Talent points spent on
      // dead ids stay on `talentBonuses` and are effectively orphaned —
      // the player can respec via in-game tools to recover them.
      if (hero.heroKey === 'sorcerer' && hero.abilities?.some((a) =>
        ['fireball', 'eruption', 'frostbolt', 'arcane_shield', 'arcane_might', 'frost_wave'].includes(a.id),
      )) {
        hero.abilities = getHeroAbilities('sorcerer');
      }
      if (hero.stats.rage === undefined) hero.stats.rage = 0;
      if (hero.stats.maxRage === undefined) hero.stats.maxRage = hero.heroClass === 'warrior' ? 100 : 0;
      if (hero.stats.energy === undefined) hero.stats.energy = hero.heroClass === 'assassin' ? ASSASSIN_MAX_ENERGY : 0;
      if (hero.stats.maxEnergy === undefined) hero.stats.maxEnergy = hero.heroClass === 'assassin' ? ASSASSIN_MAX_ENERGY : 0;
      if (hero.stats.spellPower === undefined) hero.stats.spellPower = 0;
      if (hero.stats.arrowCount === undefined) hero.stats.arrowCount = hero.heroClass === 'archer' ? ARCHER_MAX_ARROWS : 0;
      if (hero.stats.maxArrows === undefined) hero.stats.maxArrows = hero.heroClass === 'archer' ? ARCHER_MAX_ARROWS : 0;
      if (hero.stats.reloadTimer === undefined) hero.stats.reloadTimer = 0;
      if (hero.respawnUsesLeft === undefined) hero.respawnUsesLeft = 0;
      if (hero.permanentlyDead === undefined) hero.permanentlyDead = false;
      // Clear any pending temporary buff flags that got serialized mid-buff
      const _h = hero as any;
      if (_h._berserkActive) {
        if (_h._berserkCritDmgBuff) hero.stats.critDamage -= _h._berserkCritDmgBuff;
        if (_h._berserkSpdMult && _h._berserkSpdMult > 1) hero.stats.speed /= _h._berserkSpdMult;
        _h._berserkActive = false; _h._berserkCritDmgBuff = 0; _h._berserkSpdMult = 1;
      }
      if (_h._furyActive && _h._furyOriginalAttack !== undefined) {
        hero.stats.attack = _h._furyOriginalAttack;
        _h._furyActive = false; _h._furyOriginalAttack = undefined;
      }
      if (_h._stoneFormActive && _h._stoneFormDefBonus) {
        hero.stats.defense = Math.max(0, hero.stats.defense - _h._stoneFormDefBonus);
        _h._stoneFormActive = false; _h._stoneFormDefBonus = 0; _h._stoneFormTimer = 0;
      }
      if ((_h._arcaneMightSpBuff || 0) > 0) {
        hero.stats.spellPower = Math.max(0, (hero.stats.spellPower || 0) - _h._arcaneMightSpBuff);
        _h._arcaneMightSpBuff = 0;
      }
      // Sanity-check stat values that compound-bug could inflate unreasonably.
      // Reconstruct expected critDamage & speed from class base + level growth.
      {
        const classKey = (hero.heroKey ?? hero.heroClass) as keyof typeof CLASS_BASE_STATS;
        const base = CLASS_BASE_STATS[classKey] ?? CLASS_BASE_STATS[hero.heroClass];
        const growth = CLASS_STAT_GROWTH[classKey as keyof typeof CLASS_STAT_GROWTH] ?? CLASS_STAT_GROWTH[hero.heroClass as keyof typeof CLASS_STAT_GROWTH];
        if (base && growth) {
          // Expected stat = base + (levels gained * growth) — equipment not included but items give < 2.0 total
          const lvl = Math.max(1, hero.level);
          const expectedCrit = base.critDamage + (lvl - 1) * (growth.critDamage || 0);
          const expectedSpd = base.speed + (lvl - 1) * (growth.speed || 0);
          const MAX_ITEM_CRIT_BONUS = 3.0;   // generous ceiling for items/tomes
          const MAX_ITEM_SPD_BONUS = 2.0;
          const expectedAtk = base.attack + (lvl - 1) * (growth.attack || 0);
          // Allow up to 20× level-scaled attack from items/tomes before flagging as corrupt
          if (hero.stats.critDamage > expectedCrit + MAX_ITEM_CRIT_BONUS) {
            hero.stats.critDamage = expectedCrit;
          }
          if (hero.stats.speed > expectedSpd + MAX_ITEM_SPD_BONUS) {
            hero.stats.speed = expectedSpd;
          }
          if (hero.stats.attack > expectedAtk * 20 + 1000) {
            hero.stats.attack = Math.round(expectedAtk);
          }
          // Symmetric low-side fix — if a serialization path dropped the
          // attack to below half of the level-1 baseline (e.g. Valka's early
          // save cycles before her heroKey was fully wired into the stats
          // tables), reconstitute it from base + growth. Tested observation:
          // Valka was auto-attacking for 1 damage on the first batch of runs
          // after part-100; this guard heals any stuck saves at next load.
          const ATK_LOW_FLOOR = Math.max(1, Math.floor(base.attack * 0.5));
          if ((hero.stats.attack ?? 0) < ATK_LOW_FLOOR) {
            hero.stats.attack = Math.round(expectedAtk);
          }
          // Same for baseStats which `getEffectiveStats` also reads from.
          if (hero.baseStats && (hero.baseStats.attack ?? 0) < ATK_LOW_FLOOR) {
            hero.baseStats.attack = base.attack;
          }
          if (hero.baseStats && (hero.baseStats.maxHp ?? 0) < Math.floor(base.maxHp * 0.5)) {
            hero.baseStats.maxHp = base.maxHp;
          }
        }
      }
      // Migrate Poison Trap → Vine Snare for archer saves
      if (hero.heroClass === 'archer' || hero.heroKey === 'archer') {
        const trapAbility = hero.abilities.find((a) => a.id === 'trap');
        if (trapAbility) {
          trapAbility.id = 'vine_snare';
          trapAbility.name = 'Vine Snare';
          trapAbility.effectType = 'vine_snare' as any;
          trapAbility.iconKey = 'spell_poison_trap';
        }
        if ((hero as any).talentBonuses?.['trap'] !== undefined) {
          (hero as any).talentBonuses['vine_snare'] = (hero as any).talentBonuses['trap'];
          delete (hero as any).talentBonuses['trap'];
        }
      }
      // Normalize ability order + sync descriptions/names/damage from canonical (progression.ts may have changed)
      {
        const canonicalAbilities = getHeroAbilities(hero.heroKey ?? hero.heroClass);
        const canonicalOrder = canonicalAbilities.map((a) => a.id);
        hero.abilities.sort((a, b) => {
          const ai = canonicalOrder.indexOf(a.id);
          const bi = canonicalOrder.indexOf(b.id);
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
        // Sync mutable fields from canonical so save data always shows latest descriptions.
        // iconKey is included so re-skinned abilities (Vine Snare → Poison Trap) update on load
        // even for existing saves.
        for (const savedAbility of hero.abilities) {
          const canonical = canonicalAbilities.find((ca) => ca.id === savedAbility.id);
          if (canonical) {
            savedAbility.description = canonical.description;
            savedAbility.name = canonical.name;
            savedAbility.damage = canonical.damage;
            savedAbility.healAmount = canonical.healAmount;
            savedAbility.iconKey = canonical.iconKey;
          }
        }
      }
      // Inject Lich Form ability for necromancers with soul_leech rank 7
      this.syncNecromancerLichForm(hero);
      // Cap any talent bonuses that exceed their maxRank (saves from before caps were added)
      for (const abilityId of Object.keys(hero.talentBonuses)) {
        const info = getTalentUpgradeInfo(abilityId);
        if (info?.maxRank !== undefined && hero.talentBonuses[abilityId] > info.maxRank) {
          hero.talentBonuses[abilityId] = info.maxRank;
        }
      }
      // Migrate equipment: old slot names → new slot layout
      const eq = hero.equipment as any;
      if (eq.weapon !== undefined || eq.weapon1 === undefined) {
        // Old format detected — migrate to new slots
        const oldWeapon = eq.weapon ?? null;
        const oldOffhand = eq.offhand ?? null;
        const oldArmor = eq.armor ?? null;
        delete eq.weapon; delete eq.offhand; delete eq.helmet; delete eq.boots;
        delete eq.accessory1; delete eq.accessory2; delete eq.trinket;
        eq.weapon1 = eq.weapon1 ?? oldWeapon;
        eq.weapon2 = eq.weapon2 ?? oldOffhand;
        eq.armor = oldArmor;
        eq.reserved = null;
        eq.relic1 = eq.relic1 ?? null;
        eq.relic2 = eq.relic2 ?? null;
        eq.relic3 = eq.relic3 ?? null;
        eq.relic4 = eq.relic4 ?? null;
      }
    }
    for (const item of p.inventory) {
      if (item.levelRequirement === undefined) item.levelRequirement = 1;
      // Migrate old slot names on inventory items
      const s = item.slot as string;
      if (s === 'weapon') item.slot = 'weapon1';
      else if (s === 'offhand') item.slot = 'weapon2';
      else if (s === 'helmet' || s === 'boots') item.slot = 'armor';
      else if (s === 'accessory1' || s === 'accessory2' || s === 'trinket') item.slot = 'relic1';
    }
    // Repair sprite keys — re-compute from current maps so icons show correctly on old saves
    const repairItem = (item: Equipment) => { repairArmorSpriteKey(item); repairWeaponSpriteKey(item); };
    for (const item of p.inventory) repairItem(item);
    for (const hero of p.heroes) {
      const eq = hero.equipment;
      if (eq.armor) repairArmorSpriteKey(eq.armor);
      if (eq.weapon1) repairWeaponSpriteKey(eq.weapon1);
      if (eq.weapon2) repairWeaponSpriteKey(eq.weapon2);
    }
    for (const item of (p.merchant?.equipmentItems ?? [])) repairItem(item as any);
    // Tome system migration
    if ((p as any).tomes === undefined) (p as any).tomes = createDefaultTomes();
    if ((p as any).chosenTomeTypes === undefined) (p as any).chosenTomeTypes = [];
    // Talent system migration
    for (const hero of p.heroes) {
      if (hero.talentBonuses === undefined) (hero as any).talentBonuses = {};
    }
    // Merchant / consumable system migration
    if (p.activeConsumables === undefined) (p as any).activeConsumables = [];
    if (p.merchant === undefined) (p as any).merchant = { items: [], equipmentItems: [], lastRefreshWave: 0 };
    if (p.merchant.equipmentItems === undefined) (p.merchant as any).equipmentItems = [];
    if (p.merchant.mysteryItems === undefined) (p.merchant as any).mysteryItems = [];
    if (p.merchant.lastMysteryRefreshWave === undefined) (p.merchant as any).lastMysteryRefreshWave = 0;
    // Stay-aware merchant clocks (monotonic run-wave counters). Old saves stored
    // absolute globalWave() timestamps which bounced backward on stays and
    // stalled the refresh cycle. Starting fresh at 0 is safe — worst case the
    // player waits an extra wave or two after the migration; nothing stays
    // stuck.
    if ((p.merchant as any).wavesSinceRefresh === undefined) (p.merchant as any).wavesSinceRefresh = 0;
    if ((p.merchant as any).wavesSinceMysteryRefresh === undefined) (p.merchant as any).wavesSinceMysteryRefresh = 0;

    // Migrate old per-slot achievements/lifetimeStats into account-wide storage
    const oldStats = (p as any).lifetimeStats;
    const oldAch = (p as any).achievements;
    if (oldStats) {
      const acctLs = this.account.lifetimeStats;
      // Merge old per-slot stats into account (take max / sum as appropriate)
      acctLs.totalKillsAllTime = Math.max(acctLs.totalKillsAllTime, oldStats.totalKillsAllTime || 0);
      acctLs.totalBossKills = Math.max(acctLs.totalBossKills, oldStats.totalBossKills || 0);
      acctLs.totalGoldEarned = Math.max(acctLs.totalGoldEarned, oldStats.totalGoldEarned || 0);
      acctLs.totalDeaths = Math.max(acctLs.totalDeaths, oldStats.totalDeaths || 0);
      acctLs.totalRunsStarted = Math.max(acctLs.totalRunsStarted, oldStats.totalRunsStarted || 0);
      acctLs.highestWaveReached = Math.max(acctLs.highestWaveReached, oldStats.highestWaveReached || 0);
      acctLs.highestHeroLevel = Math.max(acctLs.highestHeroLevel, oldStats.highestHeroLevel || 0);
      acctLs.totalItemsFound = Math.max(acctLs.totalItemsFound, oldStats.totalItemsFound || 0);
      acctLs.legendaryItemsFound = Math.max(acctLs.legendaryItemsFound, oldStats.legendaryItemsFound || 0);
      acctLs.epicItemsFound = Math.max(acctLs.epicItemsFound, oldStats.epicItemsFound || 0);
      acctLs.totalCrits = Math.max(acctLs.totalCrits, oldStats.totalCrits || 0);
      acctLs.totalAbilitiesUsed = Math.max(acctLs.totalAbilitiesUsed, oldStats.totalAbilitiesUsed || 0);
      acctLs.totalGoldSpent = Math.max(acctLs.totalGoldSpent, oldStats.totalGoldSpent || 0);
      acctLs.totalMaterialsSpent = Math.max(acctLs.totalMaterialsSpent, oldStats.totalMaterialsSpent || 0);
      acctLs.beastKills = Math.max(acctLs.beastKills, oldStats.beastKills || 0);
      acctLs.undeadKills = Math.max(acctLs.undeadKills, oldStats.undeadKills || 0);
      acctLs.orcKills = Math.max(acctLs.orcKills, oldStats.orcKills || 0);
      acctLs.demonKills = Math.max(acctLs.demonKills, oldStats.demonKills || 0);
      acctLs.elementalKills = Math.max(acctLs.elementalKills, oldStats.elementalKills || 0);
      acctLs.humanKills = Math.max(acctLs.humanKills, oldStats.humanKills || 0);
      acctLs.dragonKills = Math.max(acctLs.dragonKills, oldStats.dragonKills || 0);
      acctLs.consumablesBought = Math.max(acctLs.consumablesBought, oldStats.consumablesBought || 0);
      acctLs.itemsSold = Math.max(acctLs.itemsSold, oldStats.itemsSold || 0);
      acctLs.tomesMaxed = Math.max(acctLs.tomesMaxed, oldStats.tomesMaxed || 0);
      acctLs.bestRunScore = Math.max(acctLs.bestRunScore, oldStats.bestRunScore || 0);
      acctLs.fastBossKills = Math.max(acctLs.fastBossKills, oldStats.fastBossKills || 0);
      if (oldStats.classesUsed) {
        for (const cls of oldStats.classesUsed) {
          if (!acctLs.classesUsed.includes(cls)) acctLs.classesUsed.push(cls);
        }
      }
      delete (p as any).lifetimeStats;
    }
    if (oldAch) {
      // Migrate old completed/claimed achievements into new account achievements
      for (const old of oldAch) {
        const acctAch = this.account.achievements.find((a) => a.id === old.id);
        if (acctAch) {
          acctAch.current = Math.max(acctAch.current, old.current || 0);
          if (old.completed) acctAch.completed = true;
          if (old.claimed) acctAch.claimed = true;
        }
      }
      delete (p as any).achievements;
    }
    // Backfill lifetime stats from player data (first-time migration)
    const als = this.account.lifetimeStats;
    if (als.totalKillsAllTime === 0 && p.totalKills > 0) als.totalKillsAllTime = p.totalKills;
    if (als.highestWaveReached === 0 && p.highestLevel > 1) als.highestWaveReached = (p.highestLevel - 1) * 10;
    if (als.bestRunScore === 0 && p.highScore > 0) als.bestRunScore = p.highScore;
    // Restore account-level data — these are authoritative at account scope, not per-slot
    if (this.account.tokens !== undefined) p.resources.loot = this.account.tokens;
    if (this.account.passiveTree) p.passiveTree = { ...this.account.passiveTree };
    this.saveAccount();
  }

  // ---------- Server API helpers ----------

  private authHeaders(): Record<string, string> {
    const token = AuthService.getInstance().getToken();
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
  }

  /** Initialise all game data from server. Must be awaited after login before gameplay. */
  async init(): Promise<void> {
    this.saveCache.clear();
    this.sessionCache.clear();
    this.isInitialized = false;
    // Account-fetch guard reset — until the server confirms the account row
    // (success OR explicit 200-OK with null), pushes are blocked. Without
    // this, a network blip during init() leaves the in-memory account at
    // its empty default, and the next save() debounce would overwrite the
    // user's real server-side account (armory levels, passive tree, tokens)
    // with the empty default. Reset here on every init so a re-init has
    // to re-confirm freshness.
    this.accountFetchedFromServer = false;
    // Reset in-memory state to defaults BEFORE fetching — otherwise a previous user's
    // account/player stays in the singleton and a fresh account inherits their tokens,
    // passive tree, lifetime stats, etc. when the server returns no data for the new user.
    this.account = createDefaultAccountData();
    this.player = createNewPlayer(0);

    const auth = AuthService.getInstance();
    if (!auth.isLoggedIn()) {
      this.isInitialized = true;
      return;
    }

    // Fetch all save slots — independent try/catch so account failure can't wipe saves
    try {
      const savesRes = await fetch(`${getApiBase()}/api/saves`, { headers: this.authHeaders() });
      if (savesRes.ok) {
        const { saves } = await savesRes.json() as { saves: Array<{ slot: number; data: PlayerState }> };
        for (const s of saves) this.saveCache.set(s.slot, s.data);
      }
    } catch { /* offline — ignore */ }

    // One-time migration: if server has no saves but localStorage has old data, import it
    if (this.saveCache.size === 0) {
      this._migrateFromLocalStorage();
    }

    // Fetch account data — independent try/catch so a save-fetch blip doesn't lose account.
    // Mark `accountFetchedFromServer = true` ONLY when the server returns a 2xx response
    // (whether `account` is populated or explicitly null for fresh users). On any failure
    // (throw, !ok, parse error) we leave the flag false so the destructive push is blocked.
    try {
      const accountRes = await fetch(`${getApiBase()}/api/saves/account`, { headers: this.authHeaders() });
      if (accountRes.ok) {
        const { account } = await accountRes.json() as { account: AccountData | null };
        if (account) {
          if (!account.lifetimeStats) account.lifetimeStats = createDefaultLifetimeStats();
          if (!account.achievements) account.achievements = [];
          for (const def of ACHIEVEMENTS) {
            if (!account.achievements.find((a) => a.id === def.id)) {
              account.achievements.push({ id: def.id, current: 0, completed: false, claimed: false });
            }
          }
          this.account = account;
        }
        // 2xx success — even null `account` (no server-side row yet) means
        // the server is reachable and the absence is authoritative, so a
        // first-time push is safe and expected.
        this.accountFetchedFromServer = true;
      }
    } catch { /* offline — ignore */ }

    // Set player from cached slot
    const slot = this.player.saveSlot;
    const cached = this.saveCache.get(slot);
    if (cached) {
      this.player = { ...cached };
      try { this.migratePlayer(); } catch { /* ignore migration errors on malformed legacy data */ }
    } else {
      this.player = createNewPlayer(slot);
    }

    // Prefetch run sessions for every known save slot in parallel — F5 recovery must
    // survive the user picking any slot from the menu after a reload, not just the
    // default one. Without this, switching to a slot whose session wasn't cached at
    // init resets waveNumber/runScore on re-entry even though gold/inventory persisted.
    const sessionSlots = new Set<number>([slot, ...this.saveCache.keys()]);
    await Promise.all([...sessionSlots].map(async (ss) => {
      try {
        const sessionRes = await fetch(`${getApiBase()}/api/saves/session/${ss}`, { headers: this.authHeaders() });
        if (sessionRes && sessionRes.ok) {
          const { session } = await sessionRes.json() as { session: { level: number; waveNumber: number; runScore: number; infernoMode: boolean; infernoZone: number; infernoScore: number; waveJustCompleted?: boolean } | null };
          if (session) this.sessionCache.set(ss, session);
        }
      } catch { /* offline — ignore */ }
    }));

    this.isInitialized = true;
  }

  /** One-time migration: import old localStorage saves to server when server has no data. */
  private _migrateFromLocalStorage(): void {
    const OLD_SLOTS = [0, 1, 2, 3, 4];
    const hasAny = OLD_SLOTS.some((i) => localStorage.getItem(`roe_save_${i}`) !== null);
    if (!hasAny) return;

    for (const i of OLD_SLOTS) {
      const raw = localStorage.getItem(`roe_save_${i}`);
      if (!raw) continue;
      try {
        const data = JSON.parse(raw) as PlayerState;
        this.saveCache.set(i, data);
        void fetch(`${getApiBase()}/api/saves/${i}`, {
          method: 'PUT',
          headers: this.authHeaders(),
          body: JSON.stringify(data),
        }).catch(() => {});
      } catch { /* ignore malformed */ }
    }

    const acctRaw = localStorage.getItem('roe_account');
    if (acctRaw) {
      try {
        this.account = JSON.parse(acctRaw) as AccountData;
      } catch { /* ignore malformed */ }
    }

    // Remove all game-data keys (auth tokens are never touched)
    for (const i of OLD_SLOTS) {
      localStorage.removeItem(`roe_save_${i}`);
      localStorage.removeItem(`roe_session_${i}`);
    }
    localStorage.removeItem('roe_account');
    localStorage.removeItem('roe_leaderboard');
    localStorage.removeItem('roe_inferno_leaderboard');
    localStorage.removeItem('roe_lb_wiped_v1');
  }

  /** Fire-and-forget: DELETE a save slot from the server. */
  private async _deleteSaveFromServer(slot: number): Promise<void> {
    if (!AuthService.getInstance().isLoggedIn()) return;
    try {
      await fetch(`${getApiBase()}/api/saves/${slot}`, {
        method: 'DELETE',
        headers: this.authHeaders(),
      });
    } catch { /* offline — ignore */ }
  }

  // ---------- Account Data (shared across all save slots) ----------

  saveAccount(): void {
    // Account data lives in this.account (in-memory). Debounce server sync.
    if (this.accountSyncTimer !== null) clearTimeout(this.accountSyncTimer);
    this.accountSyncTimer = setTimeout(() => {
      this.accountSyncTimer = null;
      void this._pushAccountToServer();
    }, GameStateManager.ACCOUNT_DEBOUNCE_MS);
  }

  /** Immediately flush any pending account sync debounce timer. Call on page close/hide. */
  flushAccountSync(): void {
    if (this.accountSyncTimer !== null) {
      clearTimeout(this.accountSyncTimer);
      this.accountSyncTimer = null;
      void this._pushAccountToServer();
    }
  }

  private async _pushAccountToServer(): Promise<void> {
    if (!AuthService.getInstance().isLoggedIn()) return;
    // Refuse to push when the local account never came back from the server
    // this session. Otherwise a transient init-time fetch failure leaves the
    // empty in-memory default account in place, and the next debounced PUT
    // would overwrite the user's real server-side data (armory levels,
    // passive tree, tokens, achievements). Read-only mode is the safer
    // default — the user keeps their progress, the next successful init
    // re-fetches and resumes normal sync.
    if (!this.accountFetchedFromServer) {
      // eslint-disable-next-line no-console
      console.warn('[ROE] Skipping account PUT — account was never fetched from server this session (offline / fetch failure). Refresh the page when online to resume sync.');
      return;
    }
    try {
      await fetch(`${getApiBase()}/api/saves/account`, {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify(this.account),
      });
    } catch { /* offline — ignore */ }
  }

  // ---------- Run Session (survives F5 / page refresh) ----------

  /** Persist current run state so a page refresh can resume exactly where it left off.
   *  waveJustCompleted=true means the wave ended cleanly; F5 should start the NEXT wave.
   *  waveJustCompleted=false (default) is only used for inferno zone transitions. */
  saveRunSession(
    waveNumber: number, runScore: number,
    infernoMode: boolean, infernoZone: number, infernoScore: number,
    waveJustCompleted = false,
    runGold = 0, runLoot = 0, runLootSpent = 0, runXp = 0, runItems = 0,
  ): void {
    const slot = this.player.saveSlot;
    const data = {
      level: this.player.currentLevel,
      waveNumber, runScore,
      infernoMode, infernoZone, infernoScore,
      waveJustCompleted,
      runGold, runLoot, runLootSpent, runXp, runItems,
    };
    this.sessionCache.set(slot, data);
    // Fire-and-forget: persist to server for F5 recovery across page reloads
    if (AuthService.getInstance().isLoggedIn()) {
      void fetch(`${getApiBase()}/api/saves/session/${slot}`, {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify(data),
      }).catch(() => { /* offline — ignore */ });
    }
  }

  /** Load a previously persisted run session. Returns null if none or if it belongs to a different level. */
  loadRunSession(): { level: number; waveNumber: number; runScore: number; infernoMode: boolean; infernoZone: number; infernoScore: number; waveJustCompleted?: boolean; runGold?: number; runLoot?: number; runLootSpent?: number; runXp?: number; runItems?: number } | null {
    const s = this.sessionCache.get(this.player.saveSlot);
    if (!s) return null;
    if (s.level !== this.player.currentLevel) return null;
    return s;
  }

  /** Clear the run session (call on defeat or when starting a genuinely new run). */
  clearRunSession(): void {
    const slot = this.player.saveSlot;
    this.sessionCache.delete(slot);
    // Fire-and-forget: remove from server
    if (AuthService.getInstance().isLoggedIn()) {
      void fetch(`${getApiBase()}/api/saves/session/${slot}`, {
        method: 'DELETE',
        headers: this.authHeaders(),
      }).catch(() => { /* offline — ignore */ });
    }
  }

  // ---------- Save / Load (multi-slot) ----------

  save(immediate = false): void {
    // Keep the save-slot card's inferno hero portraits in sync with the live roster while a
    // run is active (previously only populated in permanentInfernoDeath, so recruited heroes
    // never showed on the menu card mid-run).
    if (this.player.infernoRunActive) {
      this.player.infernoHeroData = this.player.heroes.map((h) => ({
        heroKey: h.heroKey,
        heroClass: h.heroClass,
        name: h.name,
        level: h.level,
      }));
    }
    // Always sync account-level data before writing — tokens and passive tree are authoritative at account scope
    this.account.tokens = this.player.resources.loot;
    this.account.passiveTree = { ...(this.player.passiveTree ?? {}) };
    // Account high-water stage — back-fills for users who pre-date the field so the passive-tree
    // gate checks stay unlocked on fresh slots.
    {
      let maxSeen = this.player.highestLevel ?? 0;
      for (const [, s] of this.saveCache) {
        if ((s.highestLevel ?? 0) > maxSeen) maxSeen = s.highestLevel;
      }
      if (maxSeen > (this.account.highestStageEverReached ?? 0)) {
        this.account.highestStageEverReached = maxSeen;
      }
    }
    // Write to in-memory cache (synchronous — safe for game loop)
    this.saveCache.set(this.player.saveSlot, { ...this.player });
    this.saveAccount();
    GameStateManager.onSaveCallback?.(this.player.saveSlot, this.player, immediate);
  }

  private loadSave(slot: number): PlayerState | null {
    return this.saveCache.get(slot) ?? null;
  }

  /** Highest stage reached across the entire account — used for passive tree stage gates.
   *  Considers the active player, every cached save slot, AND the account-level high-water
   *  mark so progress survives even if a save slot is deleted. */
  getAccountMaxHighestLevel(): number {
    let max = this.player.highestLevel ?? 0;
    for (const [, save] of this.saveCache) {
      if ((save.highestLevel ?? 0) > max) max = save.highestLevel;
    }
    const accountHigh = this.account.highestStageEverReached ?? 0;
    if (accountHigh > max) max = accountHigh;
    return max;
  }

  switchSaveSlot(slot: number, forceNew = false, asInfernoSave = false): void {
    this.save(); // Save current slot first (also syncs tokens + passiveTree to account)
    if (forceNew) this.saveCache.delete(slot); // Nuke stale cache so createNewPlayer is always used
    this.player = this.loadSave(slot) ? { ...this.loadSave(slot)! } : createNewPlayer(slot);
    // migratePlayer() will restore account.tokens and account.passiveTree — no need to carry manually
    this.migratePlayer();
    // Inferno save type — flagged at slot creation by the "+ New Inferno
    // Save" button on MenuScene. Stamped here so the flag survives the
    // first save and downstream BattleScene init can flip into inferno-
    // scaling mode. Only set when explicitly requested AND we just
    // created a fresh slot (forceNew); never silently flips an existing
    // normal save.
    if (forceNew && asInfernoSave) {
      this.player.infernoSave = true;
    }
    this.battle.waveNumber = 0;
    this.battle.bossDefeated = false;
  }

  resetSave(): void {
    const slot = this.player.saveSlot;
    const accountMaterials = this.player.resources.loot;
    const accountPassiveTree = this.player.passiveTree ? { ...this.player.passiveTree } : undefined;
    const freshPlayer = createNewPlayer(slot);
    // Preserve account-level runes + passive tree across save resets. Hero Armory
    // now lives on `account.armoryByHero`, which is untouched by resetSave, so no
    // explicit per-slot preservation is needed for it.
    freshPlayer.resources.loot = accountMaterials;
    freshPlayer.passiveTree = accountPassiveTree;
    this.saveCache.set(slot, { ...freshPlayer });
    this.player = freshPlayer;
    GameStateManager.onSaveCallback?.(slot, freshPlayer);
    this.battle.waveNumber = 0;
    this.battle.bossDefeated = false;
    this.battle.pendingLoot = { gold: 0, xp: 0, items: [], loot: 0, spells: [] };
  }

  getSaveSlotInfo(slot: number): {
    exists: boolean; level: number; heroLevel: number; heroes: number; faction: string;
    heroName: string; heroClass: string; heroKey?: string; setupComplete: boolean;
    heroData: { heroKey?: string; heroClass: string; name: string; level: number }[];
    infernoOnly?: boolean; infernoDeepestZone?: number; infernoDeepestWave?: number;
    infernoHeroData?: { heroKey?: string; heroClass: string; name: string; level: number }[];
  } | null {
    const data = this.loadSave(slot);
    if (!data) return null;
    // Prefer the deployed hero (last played); fall back to first hero with a key, then heroes[0]
    const deployedIdx = data.deployedHeroes?.[0] ?? 0;
    const mainHero = data.heroes[deployedIdx]
      ?? data.heroes.find((h: any) => h.heroKey)
      ?? data.heroes[0];
    // Build hero portrait data for all heroes (main first, then others)
    const allHeroes: any[] = data.heroes ?? [];
    const mainIdx = allHeroes.indexOf(mainHero) >= 0 ? allHeroes.indexOf(mainHero) : 0;
    const heroData = [
      mainHero,
      ...allHeroes.filter((_: any, i: number) => i !== mainIdx),
    ]
      .filter(Boolean)
      .map((h: any) => ({ heroKey: h.heroKey, heroClass: h.heroClass, name: h.name, level: h.level }));
    return {
      exists: true,
      level: data.currentLevel,
      heroLevel: mainHero?.level || 1,
      heroes: data.heroes.length,
      faction: data.faction || 'alliance',
      heroName: mainHero?.name || '',
      heroClass: mainHero?.heroClass || '',
      heroKey: mainHero?.heroKey,
      setupComplete: data.setupComplete ?? false,
      heroData,
      infernoOnly: data.infernoOnly,
      infernoDeepestZone: data.infernoDeepestZone,
      infernoDeepestWave: data.infernoDeepestWave,
      infernoHeroData: data.infernoHeroData,
    };
  }

  deleteSaveSlot(slot: number): void {
    this.saveCache.delete(slot);
    this.sessionCache.delete(slot);
    void this._deleteSaveFromServer(slot);
    // If deleting the active slot, auto-switch to another existing slot (if any) so the
    // deleted slot doesn't linger as the current "empty" card.
    if (this.player.saveSlot === slot) {
      const accountMaterials = this.player.resources.loot;
      const accountPassiveTree = this.player.passiveTree ? { ...this.player.passiveTree } : undefined;
      // Find the first remaining slot with data
      const nextSlot = [...this.saveCache.keys()].find(s => s >= 1 && s !== slot);
      if (nextSlot !== undefined) {
        const target = this.saveCache.get(nextSlot);
        if (target) {
          this.player = { ...target };
          this.migratePlayer();
        } else {
          this.player = createNewPlayer(nextSlot);
        }
      } else {
        this.player = createNewPlayer(slot);
      }
      this.player.resources.loot = accountMaterials;
      this.player.passiveTree = accountPassiveTree;
      this.battle.waveNumber = 0;
      this.battle.bossDefeated = false;
      this.battle.pendingLoot = { gold: 0, xp: 0, items: [], loot: 0, spells: [] };
    }
  }

  // ---------- Setup (Faction + Hero Pick) ----------

  setFaction(faction: PlayerFaction): void {
    this.player.faction = faction;
  }

  pickStarterHero(heroClass: HeroClass, heroName?: string, heroKey?: string): void {
    const name = resolveHeroName(heroClass, heroKey, heroName);
    const hero = createHero('hero_1', name, heroClass, heroKey);
    this.player.heroes = [hero];
    this.player.deployedHeroes = [0];
    this.player.setupComplete = true;
    this.save();
  }

  // ---------- Hero Management ----------

  getDeployedHeroes(): Hero[] {
    return this.player.deployedHeroes
      .map((idx) => this.player.heroes[idx])
      .filter(Boolean);
  }

  /** Max hero deployment slots based on current level (1 + 1 per 5 levels, max 3) */
  getMaxDeploySlots(): number {
    // Standard saves: deploy cap unlocks every 5 stages (1 → 2 at stage 5,
    // 3 at stage 10).
    // Inferno saves: this is an endgame mode unlocked by claiming the
    // Eternal Pyre quest, so the player has already cleared the standard
    // progression once. Skip the cap-by-stage gate — an inferno save
    // gets the full 3-slot deploy cap from the start, which lets the
    // player recruit a second / third hero immediately on zone 1.
    if (this.player.infernoSave) return 3;
    return Math.min(3, 1 + Math.floor(this.player.currentLevel / 5));
  }

  deployHero(heroIndex: number): boolean {
    if (this.player.deployedHeroes.includes(heroIndex)) return false;
    // Count only alive heroes against the slot cap — a dead hero's slot is
    // temporarily free so a new recruit can fill it while the dead hero respawns.
    const aliveDeployed = this.player.deployedHeroes.filter((idx) => {
      const h = this.player.heroes[idx];
      return h && !h.isDead && !h.permanentlyDead;
    }).length;
    if (aliveDeployed >= this.getMaxDeploySlots()) return false;
    this.player.deployedHeroes.push(heroIndex);
    return true;
  }

  undeployHero(heroIndex: number): void {
    this.player.deployedHeroes = this.player.deployedHeroes.filter((i) => i !== heroIndex);
  }

  equipItem(heroIndex: number, item: Equipment, targetSlot?: EquipmentSlot): Equipment | null {
    const hero = this.player.heroes[heroIndex];
    if (!hero) return null;
    if (hero.permanentlyDead) return null; // can't equip on a dead hero
    if (item.classRestriction && item.classRestriction !== hero.heroClass) return null;
    if (item.levelRequirement > hero.level) return null;
    // Race guard: every supported equip path takes an item that's currently in
    // the player's inventory list (HeroScene's slot picker, double-click, the
    // auto-equip pass after a kill, vendor purchase auto-equip). If the item
    // is no longer in inventory, it's already been moved (typically because
    // an autoEquipInventory call beat the slot-picker click to the punch),
    // so silently bail instead of assigning the same item to a second slot
    // and duplicating it. User-reported: dragging an item to weapon1/weapon2
    // while loot auto-equip was firing produced two copies of the same item.
    if (!this.player.inventory.some((i) => i.id === item.id)) return null;
    // Weapon type restriction
    if (item.weaponType && WEAPON_CLASS_RESTRICTIONS[item.weaponType]) {
      if (!WEAPON_CLASS_RESTRICTIONS[item.weaponType].includes(hero.heroClass)) return null;
    }
    // Armor archetype restriction
    if (item.armorArchetype && ARMOR_CLASS_RESTRICTIONS[item.armorArchetype]) {
      if (!ARMOR_CLASS_RESTRICTIONS[item.armorArchetype].includes(hero.heroClass)) return null;
    }

    // ── Dual-wield constraints ─────────────────────────────────────────────────
    if (item.slot === 'weapon1' || item.slot === 'weapon2') {
      let w1 = hero.equipment.weapon1;
      const w2 = hero.equipment.weapon2;
      const incomingType = item.weaponType;
      // 2H weapons may only go in weapon1; equipping one displaces BOTH weapon slots
      const incoming2H = incomingType ? TWO_HANDED_WEAPON_TYPES.includes(incomingType) : false;
      if (incoming2H) {
        if (targetSlot === 'weapon2') return null;
        // Displace weapon1 first (the existing mainhand), then weapon2
        if (w1) {
          this.player.inventory.push(w1);
          hero.equipment.weapon1 = undefined as any;
        }
        if (w2) {
          this.player.inventory.push(w2);
          hero.equipment.weapon2 = undefined as any;
        }
        w1 = undefined as any;
      }
      // If a 2H is already in weapon1 and incoming is not 2H, auto-displace the 2H first.
      // This allows equipping a 1H wand/sword to weapon1 or a focus/shield to weapon2
      // without the player having to manually unequip the 2H first.
      if (!incoming2H && w1?.weaponType && TWO_HANDED_WEAPON_TYPES.includes(w1.weaponType)) {
        this.player.inventory.push(w1);
        hero.equipment.weapon1 = undefined as any;
        w1 = undefined as any;
      }
      // Nothing may go in weapon2 when a 2H weapon is in weapon1 (checked with fresh ref)
      const resolvedSlot = targetSlot ?? (!hero.equipment.weapon1 ? 'weapon1' : 'weapon2');
      if (resolvedSlot === 'weapon2' && hero.equipment.weapon1?.weaponType && TWO_HANDED_WEAPON_TYPES.includes(hero.equipment.weapon1.weaponType)) return null;
      // Cannot equip 2 wands
      if (incomingType === 'wand') {
        const other = resolvedSlot === 'weapon1' ? hero.equipment.weapon2 : hero.equipment.weapon1;
        if (other?.weaponType === 'wand') return null;
      }
      // Focus is an offhand — may only go in weapon2
      if (incomingType === 'focus' && resolvedSlot === 'weapon1') return null;
      // Cannot equip 2 focuses
      if (incomingType === 'focus') {
        const other = resolvedSlot === 'weapon1' ? hero.equipment.weapon2 : hero.equipment.weapon1;
        if (other?.weaponType === 'focus') return null;
      }
      // Wand is a main-hand — may only go in weapon1
      if (incomingType === 'wand' && resolvedSlot === 'weapon2') return null;
      // Runewhisper — caster sword, weapon1 only (the runic channel only
      // ignites when the blade is held in the lead hand). Bonecleaver and
      // other 1H swords stay flexible across both slots.
      if (item.name === 'Runewhisper' && resolvedSlot === 'weapon2') return null;
    }
    // ───────────────────────────────────────────────────────────────────────────

    let slot: EquipmentSlot = targetSlot ?? item.slot;
    // Only trophy items (slot==='reserved') may go into the reserved slot
    if (slot === 'reserved' && item.slot !== 'reserved') return null;


    // Validate: target slot must be compatible with item category. Relic slots 3 & 4 are
    // quest-gated — reject any attempt to equip into a locked slot up front.
    if (targetSlot) {
      const itemCategory = item.slot.startsWith('relic') ? 'relic' : item.slot.startsWith('weapon') ? 'weapon' : item.slot;
      const targetCategory = targetSlot.startsWith('relic') ? 'relic' : targetSlot.startsWith('weapon') ? 'weapon' : targetSlot;
      if (itemCategory !== targetCategory) return null;
      if ((targetSlot === 'relic3' || targetSlot === 'relic4') && !this.isRelicSlotUnlocked(targetSlot)) return null;
    } else {
      // Auto-assign: prefer empty slot, else use item's native slot. Locked relic slots
      // are filtered out so quick-equip never picks a slot the player can't use.
      if (slot.startsWith('relic')) {
        const relicSlots = (['relic1', 'relic2', 'relic3', 'relic4'] as const)
          .filter((s) => this.isRelicSlotUnlocked(s));
        const emptyRelic = relicSlots.find((s) => !hero.equipment[s]);
        slot = (emptyRelic ?? slot) as EquipmentSlot;
      }
      if (slot.startsWith('weapon')) {
        const wType = item.weaponType;
        if (wType === 'focus') {
          // Focus is always offhand (weapon2)
          slot = 'weapon2';
        } else if (wType && TWO_HANDED_WEAPON_TYPES.includes(wType) || wType === 'wand') {
          // 2H weapons and Wand are always main-hand (weapon1)
          slot = 'weapon1';
        } else {
          // Generic weapon: first empty slot
          const weaponSlots = ['weapon1', 'weapon2'] as const;
          const emptyWeapon = weaponSlots.find((s) => !hero.equipment[s]);
          slot = (emptyWeapon ?? slot) as EquipmentSlot;
        }
      }
    }

    const oldItem = hero.equipment[slot];
    // Refuse to overwrite a locked relic
    if (oldItem && (this.player.lockedRelics ?? []).includes(oldItem.id)) return null;
    // Unique relics: prevent the same unique from living in two relic slots on the same
    // hero at once. Drops roll fresh ids per instance so the id filter on inventory below
    // doesn't catch this — we have to match on NAME + rarity. If the hero already has the
    // same-name unique elsewhere, kick that copy back to inventory first so the target
    // slot ends up as the only home. Locked-relic copies are left alone.
    if (item.rarity === 'unique' && slot.startsWith('relic')) {
      for (const otherSlot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
        if (otherSlot === slot) continue;
        const existing = hero.equipment[otherSlot];
        if (!existing || existing.rarity !== 'unique' || existing.name !== item.name) continue;
        if ((this.player.lockedRelics ?? []).includes(existing.id)) return null; // can't displace a locked copy
        this.player.inventory.push(existing);
        hero.equipment[otherSlot] = null as any;
      }
    }
    hero.equipment[slot] = item;
    this.player.inventory = this.player.inventory.filter((i) => i.id !== item.id);
    if (oldItem) {
      // Auto-sell on displacement: if the rarity matches an active auto-sell
      // toggle, the displaced gear immediately converts to gold instead of
      // landing in the inventory (otherwise the auto-sell felt half-broken
      // because every gear swap leaked an item past the toggles). User-
      // reported: "lot of time items are not sold."
      const oldRar = oldItem.rarity;
      const autoSold =
        (oldRar === 'common'   && this.player.autoSellCommon)   ||
        (oldRar === 'uncommon' && this.player.autoSellUncommon) ||
        (oldRar === 'rare'     && this.player.autoSellRare);
      if (autoSold) {
        this.player.resources.gold += oldItem.sellValue;
        this.trackItemSold();
      } else {
        this.player.inventory.push(oldItem);
      }
    }
    // Stackable runtime relics (Crescendo Idol, Bloodtally Knife, Vitality Idol,
    // etc.) reset their stacks when transferred to a NEW hero. Same-hero
    // re-equip preserves stacks — we track the last-equipped hero id on the
    // item itself so we can tell the cases apart.
    if (item.runtimeStacks !== undefined) {
      const itemAny = item as unknown as { _lastEquippedHeroId?: string };
      if (itemAny._lastEquippedHeroId !== hero.id) {
        item.runtimeStacks = 0;
      }
      itemAny._lastEquippedHeroId = hero.id;
    }
    // Vitality Idol's applied maxHp bonus is refreshed lazily in BattleScene.updateVitalityIdolHp.
    this.refreshVitalityIdolHpIfNeeded(hero, oldItem?.name, item.name);
    return oldItem;
  }

  /** If a Vitality Idol was just equipped or unequipped, recompute the persistent maxHp buff.
   *  Out-of-combat the hero isn't ticking, so we do an inline subtract/add so maxHp reflects
   *  the currently-equipped Vitality Idol stacks immediately. Mirrors BattleScene version. */
  private refreshVitalityIdolHpIfNeeded(hero: Hero, oldName: string | undefined, newName: string | undefined): void {
    if (oldName !== 'Vitality Idol' && newName !== 'Vitality Idol') return;
    let stacks = 0;
    for (const slot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
      const it = hero.equipment[slot];
      if (it?.name === 'Vitality Idol') stacks += it.runtimeStacks ?? 0;
    }
    const _h = hero as any;
    const prevApplied = (_h._vitalityIdolAppliedHp as number) ?? 0;
    // Peel other vitality-source buffs out of maxHp the same way getPureMaxHp does.
    const tomeApplied = (_h._vitalityAppliedHp as number) || 0;
    const fortApplied = (_h._fortitudeBoost as number) || 0;
    const baseMaxHp = Math.max(1, hero.stats.maxHp - prevApplied - tomeApplied - fortApplied);
    const targetApplied = Math.floor(baseMaxHp * stacks * 0.002);
    if (targetApplied === prevApplied) return;
    const delta = targetApplied - prevApplied;
    hero.stats.maxHp += delta;
    if (delta > 0) {
      hero.stats.hp = Math.min(hero.stats.maxHp, hero.stats.hp + delta);
    } else if (hero.stats.hp > hero.stats.maxHp) {
      hero.stats.hp = hero.stats.maxHp;
    }
    _h._vitalityIdolAppliedHp = targetApplied;
  }

  /** Remove an equipped item from a hero's slot and return it to inventory. */
  unequipItem(heroIndex: number, slot: EquipmentSlot): boolean {
    const hero = this.player.heroes[heroIndex];
    if (!hero) return false;
    if (hero.permanentlyDead) return false; // can't strip gear from a dead hero
    const item = hero.equipment[slot];
    if (!item) return false;
    hero.equipment[slot] = undefined as any;
    // Auto-sell on unequip — same logic as equipItem displacement so the
    // toggles consistently consume their rarity tier instead of leaking a
    // copy into inventory on every swap.
    const rar = item.rarity;
    const autoSold =
      (rar === 'common'   && this.player.autoSellCommon)   ||
      (rar === 'uncommon' && this.player.autoSellUncommon) ||
      (rar === 'rare'     && this.player.autoSellRare);
    if (autoSold) {
      this.player.resources.gold += item.sellValue;
      this.trackItemSold();
    } else {
      this.player.inventory.push(item);
    }
    // Per-item runtimeStacks stay on the item; only Vitality Idol needs an HP refresh.
    this.refreshVitalityIdolHpIfNeeded(hero, item.name, undefined);
    return true;
  }

  /**
   * Auto-equip inventory items into empty slots on deployed heroes.
   * Tries weapons, armor, then relics for each deployed hero.
   */
  autoEquipInventory(): { heroIdx: number; item: import('@roe/shared').Equipment }[] {
    const equipped: { heroIdx: number; item: import('@roe/shared').Equipment }[] = [];
    for (const heroIdx of this.player.deployedHeroes) {
      const hero = this.player.heroes[heroIdx];
      if (!hero) continue;

      // Try to fill empty weapon slots — respects per-type slot rules
      for (const wSlot of ['weapon1', 'weapon2'] as const) {
        if (hero.equipment[wSlot]) continue;
        // Never fill weapon2 when weapon1 is a 2H weapon
        if (wSlot === 'weapon2' && hero.equipment.weapon1?.weaponType &&
            TWO_HANDED_WEAPON_TYPES.includes(hero.equipment.weapon1.weaponType)) continue;
        const match = this.player.inventory.find(item => {
          if (item.slot !== 'weapon1' && item.slot !== 'weapon2') return false;
          if (item.levelRequirement > hero.level) return false;
          if (item.weaponType && WEAPON_CLASS_RESTRICTIONS[item.weaponType] &&
              !WEAPON_CLASS_RESTRICTIONS[item.weaponType].includes(hero.heroClass)) return false;
          // Enforce slot affinity:
          //   focus / shield → weapon2 only (off-hand slot, never the main hand)
          //   staff / wand   → weapon1 only
          if (item.weaponType === 'focus'  && wSlot !== 'weapon2') return false;
          if (item.weaponType === 'shield' && wSlot !== 'weapon2') return false;
          if (item.weaponType === 'staff'  && wSlot !== 'weapon1') return false;
          if (item.weaponType === 'wand'   && wSlot !== 'weapon1') return false;
          // Runewhisper — caster sword, weapon1 only. Mirrors the manual-
          // equip path's name check so auto-equip respects the same rule.
          if (item.name === 'Runewhisper' && wSlot !== 'weapon1') return false;
          return true;
        });
        if (match) {
          this.equipItem(heroIdx, match, wSlot);
          // Only show popup if the item was actually removed from inventory (equip succeeded)
          const actuallyEquipped = !this.player.inventory.some(i => i.id === match.id);
          if (actuallyEquipped) equipped.push({ heroIdx, item: match });
        }
      }

      // Try to fill empty armor slot
      if (!hero.equipment.armor) {
        const match = this.player.inventory.find(item =>
          item.slot === 'armor' &&
          item.levelRequirement <= hero.level &&
          (!item.armorArchetype || !ARMOR_CLASS_RESTRICTIONS[item.armorArchetype] || ARMOR_CLASS_RESTRICTIONS[item.armorArchetype].includes(hero.heroClass))
        );
        if (match) {
          this.equipItem(heroIdx, match, 'armor');
          equipped.push({ heroIdx, item: match });
        }
      }

      // Try to fill empty relic slots. Quest-locked slots (3 / 4 until their gate
      // quest is claimed) are skipped entirely so an auto-equip picking up a dropped
      // relic doesn't silently fail into a locked slot and lie to the popup. If all
      // unlocked slots are already full, the relic stays in inventory — exactly where
      // the player can still see/sell/transfer it.
      for (const rSlot of ['relic1', 'relic2', 'relic3', 'relic4'] as const) {
        if (hero.equipment[rSlot]) continue;
        if (!this.isRelicSlotUnlocked(rSlot)) continue;
        const match = this.player.inventory.find(item =>
          item.slot.startsWith('relic') &&
          item.levelRequirement <= hero.level
        );
        if (match) {
          const result = this.equipItem(heroIdx, match, rSlot);
          // Only surface the popup if the equip actually succeeded. equipItem returns
          // null for any rejected attempt (locked slot, restriction, etc.) — in that
          // case the inventory still holds the item and we shouldn't claim otherwise.
          if (result !== null) equipped.push({ heroIdx, item: match });
        }
      }
    }
    return equipped;
  }

  purchaseHero(heroClass: HeroClass, heroName?: string, heroKey?: string): 'ok' | 'funds' | 'error' {
    // Heroes are free — no gold cost to acquire
    const id = `hero_${heroKey ?? heroClass}_${Date.now()}`;
    const name = resolveHeroName(heroClass, heroKey, heroName);
    const hero = createHero(id, name, heroClass, heroKey);
    // Spawn at the current stage level so the recruit isn't one-shot by enemies
    if (this.player.currentLevel > 1) levelHeroTo(hero, this.player.currentLevel);
    // Talent points: heroes earn 1 pick per 5 levels (at lv 5, 10, 15, …). levelHeroTo bypasses
    // addXp's level-up detection, so we must pre-queue the picks here or recruits miss them.
    const owedTalents = Math.floor(hero.level / 5);
    if (owedTalents > 0) hero.pendingRecruitTalents = owedTalents;
    this.player.heroes.push(hero);
    // Populate the new hero's run-scoped perk snapshot immediately — snapshot
    // otherwise only runs at run-start / Rune Stone take / defeat, so a mid-run
    // recruit would fight with an empty activePassiveIds list until the next
    // checkpoint. The master perk roster lives at account scope, so a recruit
    // brings their full Armory loadout the moment they join.
    const armoryKey = this.getArmoryKey(hero);
    const entry = this.account.armoryByHero?.[armoryKey];
    hero.activePassiveIds = entry ? [...entry.passiveIds] : [];
    this.save();
    return 'ok';
  }

  addSpellToHero(heroIndex: number, spell: HeroAbility): void {
    const hero = this.player.heroes[heroIndex];
    if (!hero) return;
    if (hero.abilities.some((a) => a.id === spell.id)) return;
    hero.abilities.push({ ...spell, currentCooldown: 0 });
  }

  /** Revive a permanently dead hero using gold — deducts cost, starts 5s countdown timer */
  goldRevive(heroIndex: number): boolean {
    const hero = this.player.heroes[heroIndex];
    if (!hero || !hero.permanentlyDead) return false;
    const cost = this.getGoldReviveCost(heroIndex);
    if (this.player.resources.gold < cost) return false;
    this.player.resources.gold -= cost;
    this.trackGoldSpent(cost);
    this.checkAchievements();
    this.player.goldReviveCount = (this.player.goldReviveCount ?? 0) + 1;
    // Keep hero dead until the 5s timer expires — BattleScene handles the actual revive
    hero.permanentlyDead = false;
    hero.goldReviveTimer = 5000;
    return true;
  }

  getGoldReviveCost(heroIndex: number): number {
    const hero = this.player.heroes[heroIndex];
    if (!hero) return 0;
    const count = this.player.goldReviveCount ?? 0;
    return hero.level * GOLD_REVIVE_COST_PER_LEVEL * Math.pow(2, count);
  }

  sellItem(itemId: string): number {
    const idx = this.player.inventory.findIndex((i) => i.id === itemId);
    if (idx === -1) return 0;
    const item = this.player.inventory[idx];
    this.player.inventory.splice(idx, 1);
    this.player.resources.gold += item.sellValue;
    this.trackItemSold();
    this.checkAchievements();
    return item.sellValue;
  }

  /** Recycle an item for tokens. unique=5, legendary=2, epic=1. Returns tokens gained. */
  recycleItem(itemId: string): number {
    const idx = this.player.inventory.findIndex((i) => i.id === itemId);
    if (idx === -1) return 0;
    const item = this.player.inventory[idx];
    const tokenReward = item.rarity === 'unique' ? 5 : item.rarity === 'legendary' ? 2 : item.rarity === 'epic' ? 1 : 0;
    if (tokenReward === 0) return 0;
    this.player.inventory.splice(idx, 1);
    this.player.resources.loot += tokenReward;
    return tokenReward;
  }

  // ---------- Base Upgrades ----------

  getUpgradeCost(upgrade: BaseUpgrade): { gold: number; loot: number } {
    const base = UPGRADE_BASE_COSTS[upgrade.category] || { gold: 100, loot: 20 };
    const level = upgrade.currentLevel + 1;
    return {
      gold: Math.floor(base.gold * Math.pow(level, UPGRADE_COST_EXPONENT)),
      loot: Math.floor(base.loot * Math.pow(level, UPGRADE_COST_EXPONENT)),
    };
  }

  canAffordUpgrade(upgrade: BaseUpgrade): boolean {
    const cost = this.getUpgradeCost(upgrade);
    return this.player.resources.gold >= cost.gold && this.player.resources.loot >= cost.loot;
  }

  purchaseUpgrade(upgradeId: string): boolean {
    const upgrade = this.player.base.upgrades.find((u) => u.id === upgradeId);
    if (!upgrade || upgrade.currentLevel >= upgrade.maxLevel) return false;
    if (!this.canAffordUpgrade(upgrade)) return false;

    const cost = this.getUpgradeCost(upgrade);
    this.player.resources.gold -= cost.gold;
    this.player.resources.loot -= cost.loot;
    if (cost.gold > 0) { this.trackGoldSpent(cost.gold); this.checkAchievements(); }
    if (cost.loot > 0) this.trackMaterialsSpent(cost.loot);
    upgrade.currentLevel++;

    switch (upgrade.effect) {
      case 'base_hp':
        this.player.base.maxHp += upgrade.effectValue;
        this.player.base.hp += upgrade.effectValue;
        break;
      case 'base_regen':
        this.player.base.hpRegen += upgrade.effectValue;
        break;
      case 'hero_slots':
        this.player.base.heroSlots = Math.min(this.player.base.maxHeroSlots, this.player.base.heroSlots + upgrade.effectValue);
        break;
      case 'base_attack':
        this.player.base.baseAttack += upgrade.effectValue;
        break;
      case 'base_attack_speed':
        this.player.base.baseAttackSpeed = Math.max(500, this.player.base.baseAttackSpeed - upgrade.effectValue);
        break;
    }
    return true;
  }

  // ---------- Inferno Run Lifecycle ----------

  /** Call before launching Inferno — snapshots heroes and inventory, marks run as active.
   *  Also records `infernoEntryLevel` from the player's current stage so mob scaling tracks
   *  how far the player has progressed in base game (was hardcoded to 39, which reset every
   *  run regardless of actual power). Falls back to 39 for players still below that stage. */
  startInfernoRun(): void {
    this.player.infernoPreHeroes = JSON.parse(JSON.stringify(this.player.heroes));
    this.player.infernoPreInventory = JSON.parse(JSON.stringify(this.player.inventory));
    this.player.infernoRunActive = true;
    this.player.infernoEntryLevel = Math.max(39, this.player.currentLevel ?? 39);
    this.save();
  }

  /** Enter permanent inferno — heroes are locked forever; no snapshot taken (irreversible). */
  enterPermanentInferno(entryLevel: number): void {
    this.player.infernoOnly = true;
    this.player.infernoEntryLevel = entryLevel;
    this.player.infernoRunActive = true;
    // No snapshot — permanent inferno has no retreat
    this.save();
  }

  /** Call on natural Inferno end (death) — clears the active flag and snapshots. */
  endInfernoRun(): void {
    this.player.infernoRunActive = false;
    this.player.infernoPreHeroes = undefined;
    this.player.infernoPreInventory = undefined;
    // save() is called by the caller after this
  }

  /** Permanent inferno death — save hero data for card display, reset heroes to level 1. */
  permanentInfernoDeath(deepestZone: number, deepestWave: number): void {
    // Snapshot hero data before reset (for card display)
    this.player.infernoDeepestZone = deepestZone;
    this.player.infernoDeepestWave = deepestWave;
    this.player.infernoHeroData = this.player.heroes.map((h) => ({
      heroKey: h.heroKey,
      heroClass: h.heroClass,
      name: h.name,
      level: h.level,
    }));
    // Reset all heroes to level 1
    for (const hero of this.player.heroes) {
      hero.isDead = false;
      hero.permanentlyDead = false;
      hero.respawnTimer = 0;
      hero.respawnUsesLeft = 0;
      hero.level = 1;
      hero.xp = 0;
      hero.xpToNextLevel = 100;
      const base = CLASS_BASE_STATS[hero.heroClass as keyof typeof CLASS_BASE_STATS] ?? CLASS_BASE_STATS['warrior'];
      hero.stats = { ...hero.stats, ...base };
    }
    this.player.infernoRunActive = false;
    this.player.currentLevel = 1;
    // save() called by caller
  }

  /** Call when an Inferno run is abandoned (menu quit or F5 detected on reload).
   *  For permanent inferno slots, just clears the active flag (no snapshot to restore).
   *  For standard inferno runs, restores heroes and inventory from snapshots. */
  abandonInfernoRun(): void {
    // Sigils looted during inferno follow the same rule — unequipped drops gone,
    // equipped can be insured (10 runes each) into the account collection.
    this.applySigilInsuranceOnWipe();
    this.player.sigilsEquipped = (this.player.sigilsEquipped ?? []).filter((id) =>
      this.account.sigilCollection?.some((s) => s.id === id));
    if (this.player.infernoOnly) {
      // Permanent inferno — nothing to restore, just clear the active flag
      this.player.infernoRunActive = false;
      this.save();
      return;
    }
    if (this.player.infernoPreHeroes) {
      this.player.heroes = JSON.parse(JSON.stringify(this.player.infernoPreHeroes));
      // Keep deployed indices valid
      this.player.deployedHeroes = this.player.deployedHeroes.filter(
        (i) => i < this.player.heroes.length,
      );
    }
    if (this.player.infernoPreInventory) {
      this.player.inventory = JSON.parse(JSON.stringify(this.player.infernoPreInventory));
    }
    this.player.infernoRunActive = false;
    this.player.infernoPreHeroes = undefined;
    this.player.infernoPreInventory = undefined;
    this.save();
  }

  // ---------- Level Progression ----------

  canAdvanceLevel(): boolean {
    return this.battle.bossDefeated;
  }

  advanceLevel(): void {
    if (!this.canAdvanceLevel()) return;
    this.player.currentLevel++;
    if (this.player.currentLevel > this.player.highestLevel) {
      this.player.highestLevel = this.player.currentLevel;
    }
    // Armory Journal: every hero on the active roster gets credit for the new
    // high-water-mark stage. Tracked per-heroKey so Goomar's journal records
    // his own bests, not whoever happened to be deployed at the time.
    for (const hero of this.player.heroes) {
      const k = this.getArmoryKey(hero);
      this.trackArmoryHighestStage(k, this.player.currentLevel);
      // Phase-2 hero quest "Reach Stage N" — bumps the per-hero peak.
      this.trackHeroStage(k, this.player.currentLevel);
    }
    // Account-wide high-water mark — survives even if this save slot is later deleted, so
    // passive-tree stage gates stay unlocked on fresh slots created later.
    const prevAccountHigh = this.account.highestStageEverReached ?? 0;
    if (this.player.currentLevel > prevAccountHigh) {
      this.account.highestStageEverReached = this.player.currentLevel;
    }
    this.battle.bossDefeated = false;
    this.battle.waveNumber = 0;
    this.player.stayCount = 0;
  }

  /** "Stay" — restart the same level from wave 1, with escalating difficulty */
  stayAtLevel(): void {
    this.battle.bossDefeated = false;
    this.battle.waveNumber = 0;
    this.player.stayCount++;
  }

  /** Reset zone to wave 1 and fully revive all heroes. Used by the death Continue button. */
  resetCurrentZone(): void {
    this.battle.waveNumber = 0;
    this.battle.bossDefeated = false;
    this.player.base.hp = this.player.base.maxHp;
    // v2 talent: Eternal Hearth — +N free respawn uses per hero per run.
    const v2ExtraRespawns = getPassiveBonuses(this.player.passiveTree ?? {}).extraRespawns;
    for (const hero of this.player.heroes) {
      hero.isDead = false;
      hero.permanentlyDead = false;
      hero.respawnTimer = 0;
      hero.respawnUsesLeft = v2ExtraRespawns;
      hero.stats.hp = getEffectiveStats(hero).maxHp; // full HP incl. equipment bonuses
      hero.stats.mp = hero.stats.maxMp;
      if (hero.heroClass === 'warrior') hero.stats.rage = 0;
      if (hero.heroClass === 'assassin') hero.stats.energy = hero.stats.maxEnergy;
      hero.stats.arrowCount = hero.stats.maxArrows > 0 ? ARCHER_MAX_ARROWS : 0;
      hero.stats.reloadTimer = 0;
      // Clear v2 transient flags so they re-arm at run start.
      (hero as any)._lastBreathUsed = false;
      (hero as any)._revenantUsed = false;
      (hero as any)._twinFangCritCount = 0;
      (hero as any)._bloodfrenzyStacks = [];
    }
  }

  /** Apply sigil insurance to bag sigils before a wipe (death/restore/abandon).
   *  Insurance is now paid UP-FRONT — either at run start via the loadout modal
   *  (sigils brought from collection) or mid-run via Whiskers deposit (looted
   *  sigils). At wipe time there's no prompt: `insured` sigils return to the
   *  account collection for free, uninsured sigils drop. Collection sigils are
   *  never touched — bringing them onto the run already moved them into the
   *  bag, so they're here one way or another.
   *  `spent` is kept in the return type for backward-compat with callers that
   *  surfaced a toast; it's always 0 now since payment happened earlier. */
  private applySigilInsuranceOnWipe(): { insured: number; dropped: number; spent: number } {
    const bag = this.player.sigilBag ?? [];
    if (bag.length === 0) return { insured: 0, dropped: 0, spent: 0 };
    if (!this.account.sigilCollection) this.account.sigilCollection = [];
    let insured = 0;
    let dropped = 0;
    for (const s of bag) {
      if (s.insured) {
        // Strip the run-scoped flag as it settles back into the collection —
        // a collection sigil is never "insured" at rest; the flag only matters
        // while the sigil is in the bag during a run.
        const { insured: _drop, ...rest } = s;
        this.account.sigilCollection.push(rest as Sigil);
        insured++;
      } else {
        dropped++;
      }
    }
    this.player.sigilBag = [];
    if (insured > 0) this.flushAccountSync();
    return { insured, dropped, spent: 0 };
  }

  /** Open the "bring sigils on this run" transaction. Called by SigilLoadoutScene
   *  after the player picks up to `effectiveSigilSlots()` sigils from the account
   *  collection and toggles insurance per slot. Moves each picked sigil
   *  `collection → sigilBag` with the `insured` flag set, deducts insurance cost
   *  (10 runes × picks marked insured) from `player.resources.loot`, and equips
   *  them in order. Returns false (and mutates nothing) if the player can't
   *  afford the total insurance bill.
   *
   *  Skipping the loadout is fine — call with `picks = []` and it just seeds
   *  an empty bag. Looted sigils mid-run land in the bag with `insured: false`
   *  as before. */
  startRunWithSigils(picks: Array<{ sigilId: string; insured: boolean }>): boolean {
    if (!this.account.sigilCollection) this.account.sigilCollection = [];
    const slotCap = this.effectiveSigilSlots();
    const clamped = picks.slice(0, slotCap);

    const picked: Sigil[] = [];
    const pickedIds = new Set<string>();
    for (const { sigilId } of clamped) {
      if (pickedIds.has(sigilId)) continue; // ignore dup entries
      const idx = this.account.sigilCollection.findIndex((s) => s.id === sigilId);
      if (idx === -1) continue; // sigil no longer in collection (shouldn't happen — UI reads from same source)
      picked.push(this.account.sigilCollection[idx]);
      pickedIds.add(sigilId);
    }

    // Align picks → picked. Filter the insured flags to only entries we actually found.
    const flags = clamped
      .filter((p, i) => i < picked.length && picked[i].id === p.sigilId)
      .map((p) => p.insured);

    const insuredCount = flags.filter(Boolean).length;
    const cost = insuredCount * GameStateManager.SIGIL_INSURANCE_PRICE;
    const runes = this.player.resources.loot ?? 0;
    if (runes < cost) return false;

    // Commit: mutate state only after the afford check passes so a partial
    // failure mid-loop can't leave sigils half-migrated.
    this.player.resources.loot = runes - cost;
    if (cost > 0) this.trackMaterialsSpent(cost);
    this.player.sigilBag = this.player.sigilBag ?? [];
    this.player.sigilsEquipped = [];
    for (let i = 0; i < picked.length; i++) {
      const s = picked[i];
      const withFlag: Sigil = flags[i] ? { ...s, insured: true } : { ...s, insured: false };
      // Remove from collection (we're "bringing it" on the run)
      const collIdx = this.account.sigilCollection.findIndex((c) => c.id === s.id);
      if (collIdx !== -1) this.account.sigilCollection.splice(collIdx, 1);
      this.player.sigilBag.push(withFlag);
      this.player.sigilsEquipped.push(s.id);
    }
    this.flushAccountSync();
    this.save();
    return true;
  }

  /** True while the player is actively inside a run. Mid-run, the sigil
   *  collection becomes read-only — you can't cherry-pick fresh sigils from
   *  the account stockpile once combat has started. Stage 1 wave 0 counts as
   *  pre-run (loadout window is still open via main menu / re-enter). */
  isRunActive(): boolean {
    return this.battle.waveNumber > 0
      || this.player.currentLevel > 1
      || (this.player.stayCount ?? 0) > 0;
  }

  /** Move a single sigil out of `account.sigilCollection` and into
   *  `player.sigilBag` with an `insured` flag. Used by the MenuScene
   *  pre-run loadout prompt (fresh slot, wave 0 of stage 1) so players can
   *  commit insurance on a specific sigil before entering battle, without
   *  having to go through the full `SigilLoadoutScene` flow. Deducts
   *  `SIGIL_INSURANCE_PRICE` runes when `insured === true`.
   *  Returns false if the sigil isn't in the collection, or the player
   *  can't afford the insurance. */
  bringCollectionSigilToBag(sigilId: string, insured: boolean): boolean {
    if (!this.account.sigilCollection) return false;
    const idx = this.account.sigilCollection.findIndex((s) => s.id === sigilId);
    if (idx === -1) return false;
    const cost = insured ? GameStateManager.SIGIL_INSURANCE_PRICE : 0;
    const runes = this.player.resources.loot ?? 0;
    if (runes < cost) return false;
    const sigil = this.account.sigilCollection[idx];
    this.account.sigilCollection.splice(idx, 1);
    if (!this.player.sigilBag) this.player.sigilBag = [];
    this.player.sigilBag.push({ ...sigil, insured });
    if (cost > 0) {
      this.player.resources.loot = runes - cost;
      this.trackMaterialsSpent(cost);
    }
    this.flushAccountSync();
    this.save();
    return true;
  }

  /** Full roguelike reset on defeat — essentially a new game keeping only the
   *  starter hero class and account-level stats (highScore, loot, passiveTree). */
  resetRun(): void {
    // Armory Journal: credit each hero on the roster with a completed run
    // BEFORE we wipe the hero list. Tracks per-heroKey so Goomar's journal
    // records his own runs, not whoever replaced him after death.
    {
      const keys = this.player.heroes.map((h) => this.getArmoryKey(h));
      this.trackArmoryRunsCompleted(keys);
    }

    // Preserve account-level data that survives death
    const heroClass = this.player.heroes[0]?.heroClass || 'warrior';
    const heroKey = this.player.heroes[0]?.heroKey;
    const highScore = this.player.highScore;
    const highestLevel = Math.max(this.player.highestLevel, this.player.currentLevel);
    const loot = this.player.resources.loot; // 💎 runes persist across runs
    const passiveTree = this.player.passiveTree ? { ...this.player.passiveTree } : undefined; // 🌳 tree persists across runs
    const slot = this.player.saveSlot;
    const completedQuestIds = this.player.completedQuestIds;
    // Story quests persist across deaths — narrative progress is account-forward, not per-run.
    const storyQuests = this.player.storyQuests;

    // Create a fresh hero with the same class and heroKey
    const heroName = resolveHeroName(heroClass, heroKey);
    const freshHero = createHero('hero_1', heroName, heroClass, heroKey);

    // Wipe run-level data: gold, inventory, equipment, tomes, talents, consumables, base upgrades
    // Account-level data preserved: loot (💎 runes), highScore, highestLevel
    this.player.heroes = [freshHero];
    this.player.deployedHeroes = [0];
    this.player.inventory = [];
    this.player.resources = { gold: 50, loot };
    // Per-run mastery trackers — zeroed for the fresh run so any new feat
    // (no-deaths, no-potions, solo) can fire from a clean slate.
    this.player.runDeaths = 0;
    this.player.runPotionsUsed = 0;
    this.player.runBossesKilled = 0;
    this.player.runMaxHeroesDeployed = 1;
    this.player.base = {
      hp: BASE_STARTING_HP,
      maxHp: BASE_STARTING_HP,
      hpRegen: BASE_HP_REGEN,
      heroSlots: BASE_STARTING_HERO_SLOTS,
      maxHeroSlots: 6,
      upgrades: createDefaultBaseUpgrades(),
      baseAttack: BASE_STARTING_ATTACK,
      baseAttackSpeed: BASE_STARTING_ATTACK_SPEED,
    };
    this.player.currentLevel = 1;
    this.player.highestLevel = highestLevel;
    this.player.highScore = highScore;
    this.player.totalKills = 0;
    this.player.tomes = createDefaultTomes();
    this.player.chosenTomeTypes = [];
    // Banned books are a per-run choice — cleared so the next run's BookBanScene pick is fresh.
    this.player.bannedTomes = [];
    // Pre-run setup state — cleared so the next run flows through
    // SigilLoadout + BookBan from scratch.
    this.player.preRunSetupDone = false;
    // Shrine of Boons — active boons are per-run; clear on reset so curses don't bleed over.
    this.player.activeBoons = [];
    // Gilded Altar one-shot tracker — clear so the altar can be visited
    // once at every stage 9 / 19 / 29 / 39 / 49 of the new run.
    this.player.altarConsumedAtStages = [];
    // Horde of Goblins one-shot flag — reset so the horde card can roll again on the next run.
    this.player.hordeClearedThisRun = false;
    // Telurion pity flag — reset so each fresh run gets the guaranteed first
    // Telurion appearance at the first stage 30+ milestone (until quest claimed).
    this.player.telurionPityFiredThisRun = false;
    // Telurion one-shot flag — reset so the boss can be summoned again next run.
    this.player.telurionClearedThisRun = false;
    // Shadowlord stage-50 endboss — reset so the next run starts before
    // the endgame fight again. The cross-run best stays in
    // `shadowrealmEndlessBest` (persisted, never reset).
    this.player.shadowlordKilledThisRun = false;
    // Elite Pack cooldown tracker — clear so the next run can roll the
    // Elite Pack card immediately at stage 5+, instead of inheriting the
    // last-cleared stage from the previous run.
    this.player.lastElitePackClearedAtStage = undefined;
    // Crossroads path — paths now last 5 zones, but die alongside the run.
    this.player.activeCrossroadsMod = null;
    this.player.crossroadsModAppliedAtStage = undefined;
    this.player.activeConsumables = [];
    this.player.merchant = { items: [], equipmentItems: [], lastRefreshWave: 0, mysteryItems: [], lastMysteryRefreshWave: 0, wavesSinceRefresh: 0, wavesSinceMysteryRefresh: 0 };
    this.player.quests = createDefaultQuests();
    this.player.completedQuestIds = completedQuestIds;
    this.player.storyQuests = storyQuests ?? createDefaultStoryQuests();
    this.player.newLootCount = 0;
    this.player.stayCount = 0;
    this.player.tomeRerollUsed = false;
    this.player.tomeRerollsUsed = 0;
    this.player.talentRerollsUsed = 0;
    this.player.tomeSkipCount = 0;
    this.player.goldReviveCount = 0;
    this.player.autoRun = false;
    this.player.saveSlot = slot;
    this.player.passiveTree = passiveTree; // 🌳 rune tree survives death
    this.player.runeCheckpoint = undefined;
    this.player.runeDeepCheckpoint = undefined;
    this.player.stage10Snapshot = undefined;
    this.player.stage30Snapshot = undefined;

    // Clear inferno flags so a fresh standard run is possible
    this.player.infernoRunActive = false;
    this.player.infernoOnly = false;
    this.player.infernoEntryLevel = undefined;
    this.player.infernoPreHeroes = undefined;
    this.player.infernoPreInventory = undefined;
    this.player.infernoZone = undefined;

    // Divine Wrath cooldown is a RUN-level resource — clear it so a fresh run (incl. "Try Again")
    // starts with the ability ready instead of resuming the prior expiry timestamp.
    delete (this.player as any)._dwCdExpiry;

    // Choice log is a run-scoped history — wipe it on defeat/new run
    this.player.choiceLog = [];

    // Sigils: equipped bag sigils can be insured (auto-paid from runes, 10 per sigil)
    // and migrate into the account collection. Unequipped bag sigils are always lost.
    // Every slot is unequipped after the wipe — insured sigils re-appear in the stash.
    this.applySigilInsuranceOnWipe();
    this.player.sigilsEquipped = [];
    this.player.newSigilCount = 0;
    // Death's Promise sigil — reset the run-scoped "2 uses per run" counter.
    delete (this.player as { _deathsPromiseUsesLeft?: number })._deathsPromiseUsesLeft;

    // Reset battle state
    this.battle.waveNumber = 0;
    this.battle.bossDefeated = false;
    this.battle.enemies = [];
    this.battle.pendingLoot = { gold: 0, xp: 0, items: [], loot: 0, spells: [] };
  }

  // ---------- Rune Checkpoint ----------

  /** JSON replacer that strips runtime-only `_xxx` properties (Phaser refs, combat flags)
   *  so checkpoint serialization never hits circular-structure errors. */
  private static readonly _checkpointReplacer = (_key: string, val: unknown) =>
    _key.startsWith('_') ? undefined : val;

  /** Called automatically when the player enters stage 10 — freezes state for Rune Stone. */
  captureStage10Snapshot(): void {
    const ser = GameStateManager._checkpointReplacer;
    this.player.stage10Snapshot = {
      level: 10,
      heroes: JSON.parse(JSON.stringify(this.player.heroes, ser)),
      inventory: JSON.parse(JSON.stringify(this.player.inventory, ser)),
      gold: this.player.resources.gold,
      base: JSON.parse(JSON.stringify(this.player.base)),
      tomes: JSON.parse(JSON.stringify(this.player.tomes)),
      chosenTomeTypes: [...this.player.chosenTomeTypes],
      deployedHeroes: [...this.player.deployedHeroes],
      potionBar: JSON.parse(JSON.stringify(this.player.potionBar ?? [])),
      tomeRerollUsed: this.player.tomeRerollUsed ?? false,
      tomeSkipCount: this.player.tomeSkipCount ?? 0,
      goldReviveCount: this.player.goldReviveCount ?? 0,
      stayCount: this.player.stayCount,
    };
  }

  /** Activates the Rune Stone — copies the stage 10 snapshot into runeCheckpoint. */
  saveRunCheckpoint(runScore: number = 0): void {
    const snap = this.player.stage10Snapshot;
    if (!snap) return;
    this.player.runeCheckpoint = { ...JSON.parse(JSON.stringify(snap)), runScore };
    this.save(true); // immediate — checkpoint must reach server before player can F5
  }

  /** Restore the run to the saved checkpoint state — no exploit windows.
   *  Inventory is WIPED on restore (heroes keep what they had equipped via cp.heroes).
   *  This prevents the recycle-exploit loop: stockpile inventory → die → restore → recycle for mats → repeat. */
  restoreRunCheckpoint(): void {
    const cp = this.player.runeCheckpoint;
    if (!cp) return;
    // Sigils: same rule as inventory — unequipped bag drops are gone. Equipped bag sigils
    // can be insured (10 runes each) and migrate to the account collection so the equip
    // slot keeps pointing at a valid sigil. Without this, bag loot post-checkpoint would
    // ride a restore for free (no Whiskers deposit fee ever paid).
    this.applySigilInsuranceOnWipe();
    this.player.sigilsEquipped = (this.player.sigilsEquipped ?? []).filter((id) =>
      this.account.sigilCollection?.some((s) => s.id === id));
    this.player.heroes = JSON.parse(JSON.stringify(cp.heroes));
    this.player.inventory = [];
    this.player.resources.gold = cp.gold;
    this.player.currentLevel = 10; // always return to stage 10 regardless of checkpoint stage
    this.player.base = JSON.parse(JSON.stringify(cp.base));
    this.player.tomes = JSON.parse(JSON.stringify(cp.tomes));
    this.player.chosenTomeTypes = [...cp.chosenTomeTypes];
    this.player.deployedHeroes = [...cp.deployedHeroes];
    this.player.potionBar = JSON.parse(JSON.stringify(cp.potionBar));
    this.player.tomeRerollUsed = cp.tomeRerollUsed;
    this.player.tomeSkipCount = cp.tomeSkipCount;
    this.player.goldReviveCount = cp.goldReviveCount;
    this.player.stayCount = cp.stayCount;
    this.player.activeConsumables = []; // consumables used after checkpoint don't persist
    this.player.checkpointDeathPending = undefined;
    // Merchant — wipe stock and timestamps. Checkpoint revive drops currentLevel
    // back to 10, so any `lastRefreshWave`/`lastMysteryRefreshWave` saved from a
    // later stage is now in the future relative to globalWave(). Leaving the
    // merchant state as-is used to make the shop show "Refreshes in 19 waves"
    // and sometimes stuck half the mystery row on a dead timer.
    this.player.merchant = { items: [], equipmentItems: [], lastRefreshWave: 0, mysteryItems: [], lastMysteryRefreshWave: 0, wavesSinceRefresh: 0, wavesSinceMysteryRefresh: 0 };
    this.battle.waveNumber = 0;
    this.battle.bossDefeated = false;
  }

  /** Clear the rune checkpoint (on normal death accept or new run). */
  clearRunCheckpoint(): void {
    this.player.runeCheckpoint = undefined;
    this.player.checkpointDeathPending = undefined;
  }

  // ---------- Deep Rune Checkpoint (Stage 30) ----------

  /** Called automatically when the player enters stage 30 — freezes state for Eternal Rune Stone. */
  captureStage30Snapshot(): void {
    const ser = GameStateManager._checkpointReplacer;
    this.player.stage30Snapshot = {
      level: 30,
      heroes: JSON.parse(JSON.stringify(this.player.heroes, ser)),
      inventory: JSON.parse(JSON.stringify(this.player.inventory, ser)),
      gold: this.player.resources.gold,
      base: JSON.parse(JSON.stringify(this.player.base)),
      tomes: JSON.parse(JSON.stringify(this.player.tomes)),
      chosenTomeTypes: [...this.player.chosenTomeTypes],
      deployedHeroes: [...this.player.deployedHeroes],
      potionBar: JSON.parse(JSON.stringify(this.player.potionBar ?? [])),
      tomeRerollUsed: this.player.tomeRerollUsed ?? false,
      tomeSkipCount: this.player.tomeSkipCount ?? 0,
      goldReviveCount: this.player.goldReviveCount ?? 0,
      stayCount: this.player.stayCount,
    };
  }

  /** Activates the Eternal Rune Stone — copies the stage 30 snapshot into runeDeepCheckpoint. */
  saveDeepRunCheckpoint(runScore: number = 0): void {
    const snap = this.player.stage30Snapshot;
    if (!snap) return;
    this.player.runeDeepCheckpoint = { ...JSON.parse(JSON.stringify(snap)), runScore };
    this.save(true); // immediate — checkpoint must reach server before player can F5
  }

  restoreDeepRunCheckpoint(): void {
    const cp = this.player.runeDeepCheckpoint;
    if (!cp) return;
    // Same insurance-on-wipe treatment as the stage-10 checkpoint.
    this.applySigilInsuranceOnWipe();
    this.player.sigilsEquipped = (this.player.sigilsEquipped ?? []).filter((id) =>
      this.account.sigilCollection?.some((s) => s.id === id));
    this.player.heroes = JSON.parse(JSON.stringify(cp.heroes));
    // Inventory wiped on restore (heroes keep equipped gear via cp.heroes) — prevents recycle exploit.
    this.player.inventory = [];
    this.player.resources.gold = cp.gold;
    this.player.currentLevel = 30; // deep checkpoint always returns to stage 30
    this.player.base = JSON.parse(JSON.stringify(cp.base));
    this.player.tomes = JSON.parse(JSON.stringify(cp.tomes));
    this.player.chosenTomeTypes = [...cp.chosenTomeTypes];
    this.player.deployedHeroes = [...cp.deployedHeroes];
    this.player.potionBar = JSON.parse(JSON.stringify(cp.potionBar));
    this.player.tomeRerollUsed = cp.tomeRerollUsed;
    this.player.tomeSkipCount = cp.tomeSkipCount;
    this.player.goldReviveCount = cp.goldReviveCount;
    this.player.stayCount = cp.stayCount;
    this.player.activeConsumables = [];
    this.player.checkpointDeathPending = undefined;
    // Merchant — same wipe as the stage-10 checkpoint path: avoids stale
    // future-anchored refresh timers when currentLevel resets to 30.
    this.player.merchant = { items: [], equipmentItems: [], lastRefreshWave: 0, mysteryItems: [], lastMysteryRefreshWave: 0, wavesSinceRefresh: 0, wavesSinceMysteryRefresh: 0 };
    this.battle.waveNumber = 0;
    this.battle.bossDefeated = false;
  }

  clearDeepRunCheckpoint(): void {
    this.player.runeDeepCheckpoint = undefined;
  }

  // ---------- Auto-Mode ----------

  /** Cycle the auto-mode through Off → Auto → Full → Off.
   *  - Off:  manual everything
   *  - Auto: auto-pick books / talents within a zone
   *  - Full: same as Auto + auto-press Advance on the zone-cleared popup
   *  Keeps the legacy `autoRun` flag in sync (true when mode != 'off') so any
   *  old read sites still work; new sites should read `autoMode` directly. */
  cycleAutoMode(): 'off' | 'auto' | 'full' {
    const cur = this.player.autoMode ?? (this.player.autoRun ? 'auto' : 'off');
    const next: 'off' | 'auto' | 'full' = cur === 'off' ? 'auto' : cur === 'auto' ? 'full' : 'off';
    this.player.autoMode = next;
    this.player.autoRun = next !== 'off';
    return next;
  }

  /** Convenience accessor — reads `autoMode` falling back to the legacy
   *  `autoRun` boolean for old saves that haven't been touched yet. */
  getAutoMode(): 'off' | 'auto' | 'full' {
    return this.player.autoMode ?? (this.player.autoRun ? 'auto' : 'off');
  }

  /** Legacy entry point — kept so any external callers that haven't been
   *  migrated still flip a sensible state. New code should call
   *  `cycleAutoMode()` instead. */
  toggleAutoRun(): void {
    this.cycleAutoMode();
  }

  // ---------- Quests ----------

  /** Refresh daily / weekly quest sets if their UTC reset window has elapsed.
   *  Called by MenuScene on enter. Fresh quests replace any unclaimed ones in
   *  the same bucket — completed-but-unclaimed quests are LOST on reset by
   *  design (the reward window closed). Faction quests (the legacy evergreen
   *  ones) are now folded into the daily/weekly rotation, so this method is
   *  the only place new quests come from after the initial seed. */
  maybeRollResets(now: number = Date.now()): void {
    let changed = false;
    if (shouldRollDaily(this.player.lastDailyReset, now)) {
      // Drop existing dailies (claimed or otherwise) and roll a fresh trio.
      this.player.quests = this.player.quests.filter((q) => q.type !== 'daily');
      this.player.quests.push(...rollDailyQuests());
      this.player.lastDailyReset = lastDailyResetBefore(now);
      changed = true;
    }
    if (shouldRollWeekly(this.player.lastWeeklyReset, now)) {
      this.player.quests = this.player.quests.filter((q) => q.type !== 'weekly');
      this.player.quests.push(...rollWeeklyQuests());
      this.player.lastWeeklyReset = lastWeeklyResetBefore(now);
      // Snapshot the lifetime daily-claim count so the
      // "complete N dailies this week" weekly tracks against the new window.
      this.player.weeklyDailyClaimedSnapshot = this.player.dailyQuestsClaimedAllTime ?? 0;
      changed = true;
    }
    if (changed) this.save();
  }

  /** Bump a quest's `currentAmount`, advance status, and clamp to completed. */
  private bumpQuest(quest: Quest, by: number): void {
    if (quest.status !== 'available' && quest.status !== 'in_progress') return;
    quest.currentAmount = Math.min(quest.targetAmount, quest.currentAmount + by);
    quest.status = quest.currentAmount >= quest.targetAmount ? 'completed' : 'in_progress';
  }
  private setQuestProgress(quest: Quest, value: number): void {
    if (quest.status !== 'available' && quest.status !== 'in_progress') return;
    quest.currentAmount = Math.max(quest.currentAmount, Math.min(quest.targetAmount, value));
    quest.status = quest.currentAmount >= quest.targetAmount ? 'completed' : 'in_progress';
  }

  trackKill(enemyFaction?: string): void {
    for (const quest of this.player.quests) {
      const entry = getQuestPoolEntry(quest.id);
      if (!entry) continue;
      if (entry.kind === 'kill') this.bumpQuest(quest, 1);
      else if (entry.kind === 'kill_faction' && entry.faction === enemyFaction) this.bumpQuest(quest, 1);
    }
    // Lifetime stats
    const ls = this.account.lifetimeStats;
    ls.totalKillsAllTime++;
    if (enemyFaction) {
      const key = `${enemyFaction}Kills` as keyof typeof ls;
      if (typeof ls[key] === 'number') (ls as any)[key]++;
    }
  }

  trackBossKill(killTimeMs?: number): void {
    for (const quest of this.player.quests) {
      const entry = getQuestPoolEntry(quest.id);
      if (entry?.kind === 'kill_boss') this.bumpQuest(quest, 1);
    }
    this.account.lifetimeStats.totalBossKills++;
    if (killTimeMs !== undefined && killTimeMs < 10000) {
      this.account.lifetimeStats.fastBossKills++;
    }
    // Mastery — bump the run-scoped boss counter (caller can then call
    // `evaluateBossesInRun()` to claim Boss Crusher / Annihilator).
    this.player.runBossesKilled = (this.player.runBossesKilled ?? 0) + 1;
  }

  /** Advance any `reach_stage` quests to the highest stage reached this run. */
  trackStageReached(stage: number): void {
    for (const quest of this.player.quests) {
      const entry = getQuestPoolEntry(quest.id);
      if (entry?.kind === 'reach_stage') this.setQuestProgress(quest, stage);
    }
  }

  /** Critical hit — counts toward the `Critical Striker` daily. */
  trackCritHit(): void {
    for (const quest of this.player.quests) {
      const entry = getQuestPoolEntry(quest.id);
      if (entry?.kind === 'crit') this.bumpQuest(quest, 1);
    }
  }

  /** Potion consumed from the bar — counts toward the `Battle Mixer` daily
   *  AND the run-scoped no-potion mastery counter. */
  trackPotionUsed(): void {
    for (const quest of this.player.quests) {
      const entry = getQuestPoolEntry(quest.id);
      if (entry?.kind === 'potion') this.bumpQuest(quest, 1);
    }
    this.player.runPotionsUsed = (this.player.runPotionsUsed ?? 0) + 1;
  }

  /** Inferno run cleared — counts toward the weekly. */
  trackInfernoCleared(): void {
    for (const quest of this.player.quests) {
      const entry = getQuestPoolEntry(quest.id);
      if (entry?.kind === 'inferno_clear') this.bumpQuest(quest, 1);
    }
  }

  /** Legacy alias — old call sites pass `waveNumber` from BattleScene. The new
   *  pool tracks STAGE not wave (waves auto-completed inside Stage 1 used to
   *  trivialize the quest). Mapped to `trackStageReached` so BattleScene's
   *  existing `gsm.trackWave(this.gsm.player.currentLevel)` keeps working. */
  trackWave(stage: number): void { this.trackStageReached(stage); }

  claimQuest(questId: string): boolean {
    const quest = this.player.quests.find((q) => q.id === questId);
    if (!quest || quest.status !== 'completed') return false;
    this.player.resources.gold += quest.rewards.gold;
    this.player.resources.loot += quest.rewards.loot;
    // Distribute XP equally to all heroes (mostly 0 now — daily/weekly
    // pool entries pay rune-only — kept for any future quest that does
    // grant XP).
    if (quest.rewards.xp > 0) {
      const xpPerHero = Math.floor(quest.rewards.xp / Math.max(1, this.player.heroes.length));
      for (const hero of this.player.heroes) {
        addXp(hero, xpPerHero);
      }
    }
    quest.status = 'claimed';
    this.player.completedQuestIds.push(questId);
    // Bump the lifetime daily-claim counter + tick the weekly_dailies_10 quest
    // (only when a daily is what was just claimed).
    if (quest.type === 'daily') {
      this.player.dailyQuestsClaimedAllTime = (this.player.dailyQuestsClaimedAllTime ?? 0) + 1;
      const snapshot = this.player.weeklyDailyClaimedSnapshot ?? 0;
      const claimedThisWeek = (this.player.dailyQuestsClaimedAllTime ?? 0) - snapshot;
      for (const q of this.player.quests) {
        const entry = getQuestPoolEntry(q.id);
        if (entry?.kind === 'dailies') this.setQuestProgress(q, claimedThisWeek);
      }
    }
    // Auto-untrack the quest now that it's claimed
    this.player.trackedQuests = (this.player.trackedQuests || []).filter((id) => id !== questId);
    return true;
  }

  getCompletedQuestCount(): number {
    return this.player.quests.filter((q) => q.status === 'completed').length;
  }

  // ---------- Story Quests ----------

  /** Feed an event into the story-quest engine. Returns whatever started/completed/finished
   *  this tick so BattleScene can queue lore popups and drop reward items. Persists state on
   *  any change. No-op if storyQuests isn't seeded yet.
   *  Per user "story quests should be claimable" pass: quests that finish their last beat
   *  are NO LONGER auto-claimed here. The popup just toasts "Ready to claim" and the
   *  player must visit the Quests tab and click the Claim button. Until then, the gated
   *  unlock (relic slot / sigil / book / inferno mode) stays inactive — see
   *  `isStoryQuestClaimed` which the gates key off. */
  trackStoryQuestEvent(event: StoryQuestEvent): StoryQuestAdvanceResult {
    if (!this.player.storyQuests) this.player.storyQuests = createDefaultStoryQuests();
    const result = advanceStoryQuests(this.player.storyQuests, event);
    if (result.started.length || result.completed.length || result.finished.length) {
      this.save();
    }
    return result;
  }

  /** Public claim entry point — wired to the AchievementScene "CLAIM" button.
   *  Idempotent: re-claiming an already-claimed quest is a no-op. Returns true
   *  iff the quest transitioned from complete-but-unclaimed to claimed. */
  claimStoryQuest(questId: string): boolean {
    const quest = (this.player.storyQuests ?? []).find((q) => q.id === questId);
    if (!quest) return false;
    if (quest.claimed) return false;
    // Only claim quests whose beats are all complete.
    const allBeatsDone = quest.beats.every((b) => b.done);
    if (!allBeatsDone) return false;
    this.grantStoryQuestReward(quest);
    // Re-evaluate any quests that were waiting on this one's claim. The
    // advance_stage event only fires on stage TRANSITIONS, so if the player
    // already passed a `reach_stage` threshold BEFORE the prereq quest was
    // claimed (e.g. reached stage 40 before claiming Telurion's Lair), the
    // newly-unlocked Eternal Pyre would never see an event for that crossed
    // threshold and stay stuck at 0/1. Fire a synthetic advance_stage so any
    // quest that just became unlocked can immediately catch up to current
    // player level.
    this.trackStoryQuestEvent({
      kind: 'advance_stage',
      newStage: this.player.currentLevel,
      stage: this.player.currentLevel,
      stayCount: this.player.stayCount ?? 0,
    });
    this.save();
    return true;
  }

  /** Grant the story-quest reward. Relics evaporate on death, so rewards are sigils —
   *  they persist through the sigil bag across runs and sit in the long-term collection.
   *  A completed quest also UNLOCKS its gated sigil for future random drops via
   *  `isSigilUnlocked()`; the guaranteed copy handed out here just lets the player feel
   *  the power immediately rather than waiting for RNG.
   *  Relic-slot unlocks (shadow_veil, voidelf_rite) are handled via `isRelicSlotUnlocked`
   *  keying off `quest.claimed` — no item grant needed. Archive Beneath unlocks the
   *  Book of the Archivist tome via the TOME_DEFINITIONS.requiresQuestId path. */
  private grantStoryQuestReward(quest: StoryQuest): void {
    if (quest.claimed) return;
    if (quest.id === 'labyrinths_heart') {
      // Magmarath's Ember — always +5 % fire resist (baked-in base affix) + 1 random modifier.
      const reward = generateSpecificSigil('magmaraths_ember', 1);
      if (reward) {
        if (!this.player.sigilBag) this.player.sigilBag = [];
        this.player.sigilBag.push(reward);
        quest.rewardItemId = reward.id;
        this.player.newSigilCount = (this.player.newSigilCount ?? 0) + 1;
      }
    }
    if (quest.id === 'eternal_pyre') {
      // Eternal Pyre — capstone of the main story chain. Claiming it
      // flips the account-wide Inferno unlock so the "+ New Inferno
      // Save" option appears on MenuScene across every save slot. Once
      // earned, never lost (deleting the slot that triggered the claim
      // doesn't relock Inferno).
      this.account.infernoUnlocked = true;
      this.saveAccount();
    }
    quest.claimed = true;
    // Mirror the claim to account-wide state so new save slots inherit the unlock
    // (relic slots / gated sigils / gated tomes stay unlocked on a fresh hero).
    this.rememberClaimedStoryQuest(quest.id);
  }

  /** Record a claimed story quest on the account-wide list. Idempotent. Persists via saveAccount. */
  private rememberClaimedStoryQuest(questId: string): void {
    if (!this.account.claimedStoryQuestIds) this.account.claimedStoryQuestIds = [];
    if (this.account.claimedStoryQuestIds.includes(questId)) return;
    this.account.claimedStoryQuestIds.push(questId);
    this.saveAccount();
  }

  /** Returns true if the given story quest id has been claimed on ANY save slot on this
   *  account (checks the current slot first, then the account-wide mirror). Centralises
   *  the per-save vs account-wide fallback so gates can't get out of sync. */
  private isStoryQuestClaimed(questId: string): boolean {
    const q = (this.player.storyQuests ?? []).find((s) => s.id === questId);
    if (q?.claimed === true) return true;
    return this.account.claimedStoryQuestIds?.includes(questId) === true;
  }

  /** Predicate that the sigil generator consults to exclude quest-gated specials from the
   *  random drop pool until their StoryQuest is complete. Claimed quest → special unlocked. */
  isSigilUnlocked(special: SigilSpecial): boolean {
    if (!special.requiresQuestId) return true;
    return this.isStoryQuestClaimed(special.requiresQuestId);
  }

  /** Mirror of isSigilUnlocked for tomes — filters quest-gated tomes out of the choice pool. */
  isTomeUnlocked(id: TomeId): boolean {
    const def = TOME_DEFINITIONS[id];
    if (!def.requiresQuestId) return true;
    return this.isStoryQuestClaimed(def.requiresQuestId);
  }

  /** Relic slot gates — slots 1 & 2 are always unlocked. Slot 3 unlocks via the Veil of
   *  the Shadow Realm; slot 4 via the Rite of the Void Elves. Gate reads the account-wide
   *  claim list (via isStoryQuestClaimed), so a new save slot created on the same account
   *  inherits the unlock instead of forcing the player to redo the quest. Labyrinth's Heart
   *  stays a sigil-reward quest and no longer gates a relic slot.
   *  Legacy saves with items already in these slots get their gate quest auto-claimed
   *  during load, so nobody is retroactively locked out. */
  isRelicSlotUnlocked(slot: 'relic1' | 'relic2' | 'relic3' | 'relic4'): boolean {
    if (slot === 'relic1' || slot === 'relic2') return true;
    const questId = slot === 'relic3' ? 'shadow_veil' : 'voidelf_rite';
    return this.isStoryQuestClaimed(questId);
  }

  // ---------- Tomes ----------

  /** Pick a tome (add +1 level) and track the type choice for build limit. */
  pickTome(id: TomeId): number {
    if (!this.player.chosenTomeTypes.includes(id)) {
      this.player.chosenTomeTypes.push(id);
    }
    return pickTome(this.player.tomes, id);
  }

  /** Get effective bonus from a tome */
  getTomeBonus(id: TomeId): number {
    return getTomeBonus(this.player.tomes, id);
  }

  // ---------- Talents ----------

  /** Inject Lich Form ability for necromancer heroes that have soul_leech rank 7 */
  private syncNecromancerLichForm(hero: import('@roe/shared').Hero): void {
    if (hero.heroKey !== 'necromancer') return;
    const soulLeechRank = hero.talentBonuses?.['soul_leech'] ?? 0;
    const hasLichForm = hero.abilities.some((a) => a.id === 'lich_form');
    if (soulLeechRank >= 7 && !hasLichForm) {
      hero.abilities.push({
        id: 'lich_form', name: 'Lich Form',
        description: 'Transform into a Lich for 3s: spells cost no mana, +500% SP. Requires 30 souls. 60s CD.',
        manaCost: 0, cooldown: 60000, currentCooldown: 0,
        damage: 0, healAmount: 0, effectType: 'lich_form',
        iconKey: 'spell_soul_leech', level: 1, maxLevel: 1,
      });
    } else if (soulLeechRank < 7 && hasLichForm) {
      // Safety: remove if rank somehow dropped below 7
      hero.abilities = hero.abilities.filter((a) => a.id !== 'lich_form');
    }
  }

  /** Apply a talent point to an ability for a hero */
  applyTalent(heroIndex: number, abilityId: string): number {
    const hero = this.player.heroes[heroIndex];
    if (!hero) return 0;
    if (getAvailableTalentPoints(hero.level, hero.talentBonuses) <= 0) return 0;
    const result = applyTalentPoint(hero.talentBonuses, abilityId);
    this.syncNecromancerLichForm(hero);
    return result;
  }

  // ---------- Merchant / Consumables ----------

  /** Global wave number that increases monotonically across all levels/zones. */
  private globalWave(): number {
    return (this.player.currentLevel - 1) * WAVES_PER_ZONE + this.battle.waveNumber;
  }

  /** True if any equipped sigil carries the given special-effect id. */
  private hasEquippedSigilSpecial(specialId: string): boolean {
    const equipped = this.player.sigilsEquipped ?? [];
    const bag = this.player.sigilBag ?? [];
    for (const id of equipped) {
      const s = bag.find((b) => b.id === id);
      if (s?.special.id === specialId) return true;
    }
    return false;
  }

  /** Effective refresh interval after Hoarder rune applies (cannot drop below 2 waves).
   *  Merchant's Coin sigil: refresh 1.5× faster (interval / 1.5, rounded, min 2). */
  private merchantRefreshInterval(): number {
    const hoarderWaves = getPassiveBonuses(this.player.passiveTree ?? {}).vendorRefreshSpeed;
    let interval = Math.max(2, MERCHANT_REFRESH_INTERVAL - hoarderWaves);
    if (this.hasEquippedSigilSpecial('merchants_coin')) {
      interval = Math.max(2, Math.round(interval / 1.5));
    }
    return interval;
  }

  /** Waves remaining until the next merchant restock. Reads directly from the
   *  monotonic `wavesSinceRefresh` counter so stays (which bounce globalWave()
   *  backward) can no longer stall the clock. */
  getMerchantWavesUntilRefresh(): number {
    const wavesSince = this.player.merchant?.wavesSinceRefresh ?? 0;
    const interval = this.merchantRefreshInterval();
    return Math.max(1, Math.min(interval, interval - wavesSince));
  }

  /** Advance the merchant refresh clocks by one wave. Called once per
   *  `startNextWave` regardless of stay/advance so the refresh cadence is
   *  exactly "N waves played this run", not "N globalWave ticks" — which was
   *  breaking during stays because waveNumber resets to 0 each stay. */
  tickMerchantRefreshClocks(): void {
    const m = this.player.merchant;
    if (!m) return;
    m.wavesSinceRefresh = (m.wavesSinceRefresh ?? 0) + 1;
    m.wavesSinceMysteryRefresh = (m.wavesSinceMysteryRefresh ?? 0) + 1;
  }

  /** Refresh merchant inventory if enough waves (played this run) have passed.
   *  Returns which rows actually regenerated so BattleScene can show the refresh
   *  badge on the portrait. Purchased slots no longer count as empty — prevents
   *  the buy-out/close/reopen exploit. */
  refreshMerchantInventory(): { mainRefreshed: boolean; mysteryRefreshed: boolean } {
    if (!this.player.merchant) {
      this.player.merchant = {
        items: [], equipmentItems: [], lastRefreshWave: 0,
        mysteryItems: [], lastMysteryRefreshWave: 0,
        wavesSinceRefresh: 0, wavesSinceMysteryRefresh: 0,
      };
    }
    const m = this.player.merchant;
    const interval = this.merchantRefreshInterval();
    const MYSTERY_REFRESH_INTERVAL = 5;

    const timedRefresh = (m.wavesSinceRefresh ?? 0) >= interval;
    const mainWasEmpty = m.items.length === 0 || (m.equipmentItems ?? []).length === 0;
    let mainRefreshed = false;
    if (m.items.length === 0 || timedRefresh) {
      this._generateMerchantConsumables();
      mainRefreshed = true;
    }
    if ((m.equipmentItems ?? []).length === 0 || timedRefresh) {
      this._generateMerchantEquipment();
      mainRefreshed = true;
    }
    // Reset the counter on either a timed refresh or a first-fill so a freshly
    // rolled shop always reads a clean full 10-wave window in the UI.
    if (timedRefresh || mainWasEmpty) m.wavesSinceRefresh = 0;

    const mysteryItems = m.mysteryItems ?? [];
    const mysteryTimed = (m.wavesSinceMysteryRefresh ?? 0) >= MYSTERY_REFRESH_INTERVAL;
    let mysteryRefreshed = false;
    if (mysteryItems.length === 0 || mysteryTimed) {
      this.generateMysteryItems(); // resets wavesSinceMysteryRefresh to 0
      mysteryRefreshed = true;
    }

    return { mainRefreshed, mysteryRefreshed };
  }

  /** Generate 3 random mystery items (type revealed, contents/rarity unknown) */
  private generateMysteryItems(): void {
    const allTypes: ('weapon' | 'armor' | 'relic')[] = ['weapon', 'armor', 'relic'];
    const mysteryItems: MerchantMysteryItem[] = [];
    for (let i = 0; i < 3; i++) {
      const itemType = allTypes[Math.floor(Math.random() * allTypes.length)];
      mysteryItems.push({ itemType, purchased: false });
    }
    this.player.merchant.mysteryItems = mysteryItems;
    this.player.merchant.lastMysteryRefreshWave = this.globalWave();
    this.player.merchant.wavesSinceMysteryRefresh = 0;
  }

  /** Calculate mystery item price — linear with stage. Slope doubled from 30
   *  to 60 g/stage so late-stage gambling costs a bit more (enemy gold compounds
   *  at 1.08^stage while this stays linear, so it still falls off past s60 —
   *  worth revisiting with a super-linear curve if that reads too weak).
   *  Relics still pay a 3× markup on top. */
  getMysteryItemPrice(): number {
    const itemLevel = this.player.currentLevel;
    return Math.max(75, itemLevel * 60);
  }

  // ==================== SIGIL VENDOR (main menu cat) ====================

  /** Cost per sigil (runes). */
  static readonly SIGIL_VENDOR_PRICE = 35;
  /** Runes refunded when a sigil is recycled from the bag. */
  static readonly SIGIL_RECYCLE_REFUND = 3;
  /** Cost (runes) to move one unequipped bag sigil into the account-wide collection via in-battle Whiskers. */
  static readonly SIGIL_DEPOSIT_PRICE = 10;
  /** Cost (runes) per equipped bag sigil to insure it against death/checkpoint wipe.
   *  Auto-paid from the player's rune pool when the run ends. Insured sigils migrate
   *  into the account-wide collection so they're available on the next run. */
  static readonly SIGIL_INSURANCE_PRICE = 10;
  /** Vendor refresh interval — 30 minutes in ms (was 1 hour, halved per user
   *  feedback so the menu vendor cycles fast enough to feel alive). */
  static readonly SIGIL_VENDOR_INTERVAL_MS = 30 * 60 * 1000;

  /** Blacksmith — rune cost to bump a single equipment by +1 level. Scales linearly
   *  with current level so a Lv 5 → 6 upgrade is ~300 runes, Lv 20 → 21 is ~1.2 k. */
  static blacksmithUpgradeCost(currentLevel: number): number {
    return Math.max(50, currentLevel * 60);
  }

  /** Blacksmith — rune cost to bump a single equipment up one rarity tier.
   *  Equipment (weapons + armor) costs `level × 200`; relics cost `level × 300`
   *  since their rarity gates the unique-effect roll and is more impactful.
   *  Max once per item, gated by `rarityUpgraded` flag. */
  static blacksmithRarityCost(currentLevel: number, isRelic: boolean): number {
    return Math.max(isRelic ? 300 : 200, currentLevel * (isRelic ? 300 : 200));
  }

  /** Rune Sage — cost (runes) to forget a chosen tome. Drops its level back to 0
   *  and frees a slot in the 6-of-N pick limit so the player can pivot mid-run.
   *  Bumped 20 → 50 per user feedback (20 was too cheap given the slot freed). */
  static readonly SAGE_FORGET_BOOK_COST = 50;

  /** Drop a tome the player has invested in back to level 0, paying
   *  `SAGE_FORGET_BOOK_COST` runes. Used by the Rune Sage's "Forget a Book"
   *  service. Returns true on success.
   *
   *  Also removes the tome from `chosenTomeTypes` so the build slot is freed.
   *  Without this, a 6/6 locked build that forgets one book would have the
   *  next tome roll show only the forgotten tome (`rollTomeChoices` filters
   *  the pool to chosenTypes when the build is locked, then drops maxed
   *  ones — leaving exactly the forgotten level-0 tome to fill all 3 cards). */
  forgetTomePick(tomeId: TomeId): boolean {
    if ((this.player.resources.loot ?? 0) < GameStateManager.SAGE_FORGET_BOOK_COST) return false;
    const tome = this.player.tomes.find((t) => t.id === tomeId);
    if (!tome || tome.level === 0) return false;
    this.player.resources.loot -= GameStateManager.SAGE_FORGET_BOOK_COST;
    tome.level = 0;
    this.player.chosenTomeTypes = this.player.chosenTomeTypes.filter((id) => id !== tomeId);
    if (tomeId === 'book_archivist') {
      tome.archivistResist = undefined;
      tome.archivistResists = undefined;
    }
    this.save(true);
    return true;
  }

  /** Bump an equipped item's rarity tier by 1 (common→uncommon→rare→epic→legendary).
   *  Caps at legendary; unique items can't be upgraded. Once per item via the
   *  `rarityUpgraded` flag. Applies a flat +30 % boost to every integer-magnitude
   *  statBonus on top of the rarity label change so the upgrade reads as a real
   *  power bump. Pays `blacksmithRarityCost` from the rune pool. */
  upgradeEquipmentRarity(heroIndex: number, slot: 'weapon1' | 'weapon2' | 'armor' | 'relic1' | 'relic2' | 'relic3' | 'relic4'): { ok: boolean; cost: number; newRarity: string; reason?: 'no_runes' | 'no_item' | 'already_upgraded' | 'max_rarity' } {
    const hero = this.player.heroes[heroIndex];
    if (!hero) return { ok: false, cost: 0, newRarity: '', reason: 'no_item' };
    const item = hero.equipment[slot];
    if (!item) return { ok: false, cost: 0, newRarity: '', reason: 'no_item' };
    if (item.rarityUpgraded) {
      return { ok: false, cost: 0, newRarity: item.rarity, reason: 'already_upgraded' };
    }
    const RARITY_ORDER: ItemRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
    const idx = RARITY_ORDER.indexOf(item.rarity as ItemRarity);
    if (idx < 0 || idx >= RARITY_ORDER.length - 1) {
      return { ok: false, cost: 0, newRarity: item.rarity, reason: 'max_rarity' };
    }
    const isRelic = slot.startsWith('relic');
    const cost = GameStateManager.blacksmithRarityCost(item.level, isRelic);
    if ((this.player.resources.loot ?? 0) < cost) {
      return { ok: false, cost, newRarity: item.rarity, reason: 'no_runes' };
    }
    this.player.resources.loot -= cost;
    item.rarity = RARITY_ORDER[idx + 1];
    item.rarityUpgraded = true;
    // +30 % flat boost to every integer-magnitude stat to match the new rarity
    // tier's expected power. Fractional stats preserved (same logic as the
    // level-upgrade path).
    const sb = item.statBonuses as Record<string, number | undefined>;
    for (const k of Object.keys(sb)) {
      const v = sb[k];
      if (typeof v !== 'number' || v === 0) continue;
      if (Math.abs(v) < 1) continue;
      sb[k] = Math.ceil(v * 1.30);
    }
    this.save(true);
    return { ok: true, cost, newRarity: item.rarity };
  }

  /** Bump an equipped item's level by +1, scaling its integer statBonuses by ~10 %.
   *  Pays `blacksmithUpgradeCost(item.level)` from the rune pool. Returns true on
   *  success, false if not enough runes / item missing. Leaves fractional stats
   *  (critChance, critDamage, attackSpeedPct, etc.) untouched so the player keeps
   *  their rolled crit affixes — only base magnitude grows. */
  upgradeEquipmentLevel(heroIndex: number, slot: 'weapon1' | 'weapon2' | 'armor' | 'relic1' | 'relic2' | 'relic3' | 'relic4'): { ok: boolean; cost: number; newLevel: number } {
    const hero = this.player.heroes[heroIndex];
    if (!hero) return { ok: false, cost: 0, newLevel: 0 };
    const item = hero.equipment[slot];
    if (!item) return { ok: false, cost: 0, newLevel: 0 };
    const cost = GameStateManager.blacksmithUpgradeCost(item.level);
    if ((this.player.resources.loot ?? 0) < cost) {
      return { ok: false, cost, newLevel: item.level };
    }
    this.player.resources.loot -= cost;
    item.level += 1;
    // Scale integer-magnitude stats by ×1.10, ceil + floor at +1 so every upgrade
    // moves the number forward visibly. Fractional stats (Math.abs(v) < 1) are
    // left alone so a rolled 8 % crit chance stays 8 %.
    const sb = item.statBonuses as Record<string, number | undefined>;
    for (const k of Object.keys(sb)) {
      const v = sb[k];
      if (typeof v !== 'number' || v === 0) continue;
      if (Math.abs(v) < 1) continue; // fractional — preserve roll
      const next = Math.ceil(Math.max(v + 1, v * 1.10));
      sb[k] = next;
    }
    this.save(true);
    return { ok: true, cost, newLevel: item.level };
  }

  /** Ensure Whiskers' stock has fresh wares. The vendor is account-level — same three sigils
   *  across every save slot — so the timer only rerolls when it's actually expired. */
  refreshSigilVendor(): void {
    const now = Date.now();
    // One-time migration: if the old per-save vendor exists but the account one doesn't, lift it.
    if (!this.account.sigilVendor && this.player.sigilVendor) {
      this.account.sigilVendor = this.player.sigilVendor;
    }
    const v = this.account.sigilVendor;
    const expired = !v || now >= (v.refreshAtMs ?? 0);
    if (expired) {
      this.account.sigilVendor = {
        offered: [
          generateSigil((sp) => this.isSigilUnlocked(sp)),
          generateSigil((sp) => this.isSigilUnlocked(sp)),
          generateSigil((sp) => this.isSigilUnlocked(sp)),
        ],
        refreshAtMs: now + GameStateManager.SIGIL_VENDOR_INTERVAL_MS,
      };
      this.save();
    } else if (!v!.offered) {
      v!.offered = [];
    }
  }

  /** Recycle a sigil from the bag OR collection → refund SIGIL_RECYCLE_REFUND runes. */
  recycleSigil(sigilId: string): 'ok' | 'not_found' | 'equipped' {
    // Equipped sigils cannot be recycled — player must unequip first. Prevents
    // the awkward "I clicked recycle on my best sigil while it was active in
    // a slot" scenario, mirrors the locked-relic guard in equipItem.
    if ((this.player.sigilsEquipped ?? []).includes(sigilId)) return 'equipped';
    // Try bag first (per-save), then account collection.
    const bag = this.player.sigilBag;
    const bi = bag?.findIndex((s) => s.id === sigilId) ?? -1;
    let touchedAccount = false;
    if (bag && bi !== -1) {
      bag.splice(bi, 1);
    } else {
      const coll = this.account.sigilCollection;
      const ci = coll?.findIndex((s) => s.id === sigilId) ?? -1;
      if (!coll || ci === -1) return 'not_found';
      coll.splice(ci, 1);
      touchedAccount = true;
    }
    // v2 talent: Recycler — chance to DOUBLE the recycle refund (Recycler T1).
    // Each rank gives +5% chance; rolled per scrap. Independent rolls per rank.
    const pbRec = getPassiveBonuses(this.player.passiveTree ?? {});
    let refund = GameStateManager.SIGIL_RECYCLE_REFUND;
    if (pbRec.chanceToDoubleRecycle > 0 && Math.random() < pbRec.chanceToDoubleRecycle) {
      refund *= 2;
    }
    this.player.resources.loot = (this.player.resources.loot ?? 0) + refund;
    this.save();
    // When a sigil leaves the account collection, flush so other save slots don't re-surface it.
    if (touchedAccount) this.flushAccountSync();
    return 'ok';
  }

  /** Remaining ms until Whiskers rerolls her wares. 0 if ready. */
  sigilVendorMsUntilRefresh(): number {
    const at = this.account.sigilVendor?.refreshAtMs ?? 0;
    return Math.max(0, at - Date.now());
  }

  /** Buy one of the currently offered sigils by index. Returns 'ok' | 'funds' | 'unavailable'.
   *  Purchased sigils go straight into the account-wide collection so they survive save wipes. */
  buySigil(offerIndex: number): 'ok' | 'funds' | 'unavailable' {
    const v = this.account.sigilVendor;
    if (!v || offerIndex < 0 || offerIndex >= v.offered.length) return 'unavailable';
    if ((this.player.resources.loot ?? 0) < GameStateManager.SIGIL_VENDOR_PRICE) return 'funds';
    const sigil = v.offered[offerIndex];
    this.player.resources.loot -= GameStateManager.SIGIL_VENDOR_PRICE;
    v.offered.splice(offerIndex, 1);
    if (!this.account.sigilCollection) this.account.sigilCollection = [];
    // Stamp with the purchase time so the collection can be sorted by "bought" order.
    sigil.acquiredAt = Date.now();
    this.account.sigilCollection.push(sigil);
    this.save();
    // Account-wide mutation — flush the debounced sync immediately so a reload or a slot
    // switch doesn't lose the purchase before the 10 s debounce fires.
    this.flushAccountSync();
    return 'ok';
  }

  /** In-battle Whiskers deposit — move UNEQUIPPED bag sigils into the account collection at
   *  SIGIL_DEPOSIT_PRICE runes each. Returns the number of sigils successfully deposited.
   *  Stops early if the player runs out of runes. */
  depositUnequippedSigils(): { moved: number; spent: number } {
    const bag = this.player.sigilBag ?? [];
    const equipped = new Set(this.player.sigilsEquipped ?? []);
    if (!this.account.sigilCollection) this.account.sigilCollection = [];
    let moved = 0;
    let spent = 0;
    const keep: typeof bag = [];
    for (const s of bag) {
      if (equipped.has(s.id)) { keep.push(s); continue; }
      const runes = this.player.resources.loot ?? 0;
      if (runes < GameStateManager.SIGIL_DEPOSIT_PRICE) { keep.push(s); continue; }
      this.player.resources.loot = runes - GameStateManager.SIGIL_DEPOSIT_PRICE;
      this.account.sigilCollection.push(s);
      spent += GameStateManager.SIGIL_DEPOSIT_PRICE;
      moved++;
    }
    this.player.sigilBag = keep;
    if (moved > 0) {
      this.save();
      this.flushAccountSync();
    }
    return { moved, spent };
  }

  /** Number of bag sigils that aren't currently equipped — feeds the deposit popup. */
  countUnequippedBagSigils(): number {
    const equipped = new Set(this.player.sigilsEquipped ?? []);
    return (this.player.sigilBag ?? []).filter((s) => !equipped.has(s.id)).length;
  }

  /** Clear every equipped sigil for the current save slot. Sigils themselves stay where they live
   *  (bag or account collection). Used by the delete-save confirmation flow. */
  unequipAllSigils(): number {
    const n = this.player.sigilsEquipped?.length ?? 0;
    this.player.sigilsEquipped = [];
    if (n > 0) this.save();
    return n;
  }

  /** Clear every equipped sigil for a specific save slot (may be non-active). Works on the cache
   *  so a non-loaded slot can be cleaned before deletion without switching into it. */
  unequipAllSigilsForSlot(slot: number): number {
    if (this.player.saveSlot === slot) return this.unequipAllSigils();
    const cached = this.saveCache.get(slot);
    if (!cached) return 0;
    const n = cached.sigilsEquipped?.length ?? 0;
    cached.sigilsEquipped = [];
    this.saveCache.set(slot, cached);
    return n;
  }

  /** Count how many sigils a (possibly non-active) save slot has currently equipped. */
  equippedSigilCountForSlot(slot: number): number {
    if (this.player.saveSlot === slot) return this.player.sigilsEquipped?.length ?? 0;
    return this.saveCache.get(slot)?.sigilsEquipped?.length ?? 0;
  }

  /** Total sigil equip slots = base 1 + passive-tree unlocks. */
  effectiveSigilSlots(): number {
    const base = this.player.sigilSlots ?? 1;
    const tree = getPassiveBonuses(this.player.passiveTree ?? {}).sigilSlots;
    return Math.min(3, base + Math.floor(tree));
  }

  /** Returns the save slot number currently equipping the given sigil in ANOTHER
   *  slot (not this one), or null if the sigil is free. Collection sigils are
   *  account-wide, so without this check a single sigil could be equipped by
   *  every save slot simultaneously. Bag sigils are per-slot so they're never
   *  contested. */
  sigilEquippedByOtherSlot(sigilId: string): number | null {
    const activeSlot = this.player.saveSlot;
    for (const [slot, data] of this.saveCache) {
      if (slot === activeSlot) continue;
      if (data.sigilsEquipped?.includes(sigilId)) return slot;
    }
    return null;
  }

  /** Equip a sigil by id. Caps at effectiveSigilSlots(); returns true on success.
   *  The sigil must live in either the per-save bag or the account-wide collection.
   *  Collection sigils are blocked if another save slot is already wearing them. */
  equipSigil(sigilId: string): boolean {
    const slots = this.effectiveSigilSlots();
    if (!this.player.sigilsEquipped) this.player.sigilsEquipped = [];
    if (this.player.sigilsEquipped.includes(sigilId)) return false; // already equipped
    if (this.player.sigilsEquipped.length >= slots) return false;  // no free slot
    const inBag = this.player.sigilBag?.some((s) => s.id === sigilId) ?? false;
    const inCollection = this.account.sigilCollection?.some((s) => s.id === sigilId) ?? false;
    if (!inBag && !inCollection) return false;
    // Run-lock: once combat has started, account-collection sigils are read-only
    // — the player committed their loadout at the SigilLoadoutScene step. Only
    // looted/brought bag sigils can still be swapped into a free slot mid-run.
    if (this.isRunActive() && !inBag) return false;
    // Lock collection sigils to one slot at a time — one hero can equip each sigil.
    if (inCollection && this.sigilEquippedByOtherSlot(sigilId) !== null) return false;
    this.player.sigilsEquipped.push(sigilId);
    this.save();
    return true;
  }

  /** Unequip a sigil by id. */
  unequipSigil(sigilId: string): boolean {
    if (!this.player.sigilsEquipped) return false;
    const i = this.player.sigilsEquipped.indexOf(sigilId);
    if (i === -1) return false;
    this.player.sigilsEquipped.splice(i, 1);
    this.save();
    return true;
  }

  /** Discard a sigil (remove from bag and equipped list). */
  discardSigil(sigilId: string): boolean {
    if (!this.player.sigilBag) return false;
    const bi = this.player.sigilBag.findIndex((s) => s.id === sigilId);
    if (bi === -1) return false;
    this.player.sigilBag.splice(bi, 1);
    if (this.player.sigilsEquipped) {
      const ei = this.player.sigilsEquipped.indexOf(sigilId);
      if (ei !== -1) this.player.sigilsEquipped.splice(ei, 1);
    }
    this.save();
    return true;
  }

  /** Purchase mystery item by index. Rolls rarity on purchase. Returns the revealed equipment or null. */
  purchaseMysteryItem(index: number): Equipment | null {
    const mysteryItems = this.player.merchant.mysteryItems ?? [];
    if (index < 0 || index >= mysteryItems.length) return null;
    const item = mysteryItems[index];
    if (item.purchased) return null;

    const price = this.getMysteryItemPrice() * (item.itemType === 'relic' ? 3 : 1);
    if (this.player.resources.gold < price) return null;
    this.player.resources.gold -= price;
    this.trackGoldSpent(price);
    item.purchased = true;

    // Roll rarity: 55/30/8/4/2.8/0.2% (common/uncommon/rare/epic/legendary/unique)
    const roll = Math.random() * 100;
    let rarity: ItemRarity;
    if (roll < 55) rarity = 'common';
    else if (roll < 85) rarity = 'uncommon';
    else if (roll < 93) rarity = 'rare';
    else if (roll < 97) rarity = 'epic';
    else if (roll < 99.8) rarity = 'legendary';
    else rarity = 'unique';

    const itemLevel = this.player.currentLevel;
    const lootTier = rarity === 'legendary' || rarity === 'unique' ? 5 : rarity === 'epic' ? 4 : rarity === 'rare' ? 3 : rarity === 'uncommon' ? 2 : 1;

    // Unique tier is relic-only; downgrade non-relic unique rolls to legendary
    let effectiveRarity = rarity;
    let effectiveType = item.itemType;
    if (rarity === 'unique' && item.itemType !== 'relic') {
      effectiveRarity = 'legendary';
    }
    // Relics can't drop below rare — upgrade common/uncommon rolls
    if (item.itemType === 'relic' && (effectiveRarity === 'common' || effectiveRarity === 'uncommon')) {
      effectiveRarity = 'rare';
    }
    // generateEquipmentForType handles unique relic generation directly from UNIQUE_RELIC_POOL
    const equipment = generateEquipmentForType(effectiveType, itemLevel, lootTier, 'boss', effectiveRarity);

    this.player.inventory.push(equipment);
    this.player.newLootCount = (this.player.newLootCount || 0) + 1;
    this.trackItemFound(equipment.rarity);
    this.checkAchievements();
    return equipment;
  }

  // ── Hero Armory (account-level per heroKey) ────────────────────────────
  //
  // Hero Armory is account-tier progression: each heroKey accumulates XP + has
  // up to 3 permanently-equipped passives. XP is granted at end of run from a
  // share of the in-run XP each hero earned (main-slot 5 %, recruits 1 %).
  // Respec wipes all three picks for a flat 150-rune cost. Picks snapshot into
  // the run-scoped `hero.activePassiveIds` at run start so mid-run respecs
  // can't retro-apply (would let players swap passives per biome).

  /** Identity key used by the armory. Unique heroes (heroKey present) each
   *  have their own state; class-only heroes share state per heroClass. */
  private getArmoryKey(hero: Hero): string {
    return hero.heroKey ?? hero.heroClass;
  }

  /** Read the stored state for a heroKey, returning an empty default if the
   *  player has never played / unlocked that hero before. Backed by
   *  `account.armoryByHero` so progression is shared across save slots. */
  getArmoryState(heroKey: string): ArmoryHeroState {
    const map = this.account.armoryByHero;
    const existing = map?.[heroKey];
    return existing ? { ...existing, passiveIds: [...existing.passiveIds] } : emptyArmoryHeroState();
  }

  /** Ensure `account.armoryByHero[heroKey]` exists and return a mutable reference. */
  private ensureArmoryEntry(heroKey: string): ArmoryHeroState {
    if (!this.account.armoryByHero) this.account.armoryByHero = {};
    if (!this.account.armoryByHero[heroKey]) this.account.armoryByHero[heroKey] = emptyArmoryHeroState();
    const entry = this.account.armoryByHero[heroKey]!;
    // Journal is optional for save-compat (old saves predate it). Initialise
    // on first touch so every incrementer below can assume it's present.
    if (!entry.journal) {
      entry.journal = { runsCompleted: 0, highestStage: 0, totalKills: 0, totalBossKills: 0 };
    }
    return entry;
  }

  /** Increment the kill count in this hero's Armory Journal. Boss kills also
   *  bump the `totalBossKills` sub-counter. Debounced sync — the account is
   *  flushed through the normal save path, this doesn't fire-and-forget. */
  trackArmoryKill(heroKey: string, isBoss: boolean): void {
    const entry = this.ensureArmoryEntry(heroKey);
    if (!entry.journal) return; // defensive
    entry.journal.totalKills += 1;
    if (isBoss) entry.journal.totalBossKills += 1;
  }

  /** Bump the "highest stage reached" journal stat for a hero. Called on
   *  advanceLevel so every deployed hero's journal tracks the peak. */
  trackArmoryHighestStage(heroKey: string, stage: number): void {
    const entry = this.ensureArmoryEntry(heroKey);
    if (!entry.journal) return;
    if (stage > entry.journal.highestStage) entry.journal.highestStage = stage;
  }

  /** Bump `runsCompleted` for every hero that was on the active roster at the
   *  time the run ended (death / abandon). Called from resetRun BEFORE heroes
   *  are wiped, so we know who to credit. */
  trackArmoryRunsCompleted(heroKeys: string[]): void {
    for (const k of heroKeys) {
      const entry = this.ensureArmoryEntry(k);
      if (entry.journal) entry.journal.runsCompleted += 1;
    }
  }

  /** Pretty summary used by the Armory UI — combines level, total xp, progress
   *  into the current level, xp needed for the next level, and unlock info. */
  getArmoryInfo(heroKey: string): {
    level: number;
    xp: number;
    xpIntoLevel: number;
    xpToNext: number;
    unlockedSlots: number;
    passiveIds: string[];
    atMax: boolean;
  } {
    const s = this.getArmoryState(heroKey);
    const level = s.level;
    // Use the cached cumulative-xp lookup — the old Array.from reduce was O(level)
    // per call which gets expensive now that MAX_LEVEL is 5000.
    const totalForCurrent = armoryTotalXpForLevel(level);
    const xpIntoLevel = Math.max(0, s.xp - totalForCurrent);
    const xpToNext = armoryXpToNext(level);
    return {
      level, xp: s.xp, xpIntoLevel, xpToNext,
      unlockedSlots: unlockedPassiveSlots(level),
      passiveIds: [...s.passiveIds],
      atMax: level >= ARMORY_MAX_LEVEL,
    };
  }

  /** Grant Armory XP to all heroes deployed in the run that just ended.
   *  Call once per run end with each hero's earned XP (main slot flagged).
   *  Caller is responsible for persisting via save(). */
  grantArmoryXpForRun(entries: { hero: Hero; runXpEarned: number; isMain: boolean }[]): void {
    for (const { hero, runXpEarned, isMain } of entries) {
      if (!hero || runXpEarned <= 0) continue;
      const key = this.getArmoryKey(hero);
      const entry = this.ensureArmoryEntry(key);
      const share = isMain ? ARMORY_MAIN_XP_SHARE : ARMORY_RECRUIT_XP_SHARE;
      const delta = Math.floor(runXpEarned * share);
      if (delta <= 0) continue;
      entry.xp += delta;
      entry.level = resolveArmoryLevel(entry.xp, entry.level);
    }
  }

  /** Incremental per-hero XP grant — called from BattleScene each time a hero
   *  earns in-run XP. Resolves main vs recruit by checking whether the hero
   *  is in `player.deployedHeroes[0]`. Caller handles persistence. */
  grantArmoryXp(hero: Hero, xpToShare: number): void {
    if (!hero || xpToShare <= 0) return;
    const mainIdx = this.player.deployedHeroes[0];
    const heroIdx = this.player.heroes.indexOf(hero);
    const isMain = heroIdx >= 0 && heroIdx === mainIdx;
    this.grantArmoryXpForRun([{ hero, runXpEarned: xpToShare, isMain }]);
  }

  /** Direct Armory XP grant — bypasses the run-share fraction so quest
   *  rewards land at face value. Hero quest claims use this to deposit
   *  the bronze/silver/gold tier reward straight into the heroKey's entry. */
  grantDirectArmoryXp(heroKey: string, xp: number): void {
    if (!heroKey || xp <= 0) return;
    const entry = this.ensureArmoryEntry(heroKey);
    entry.xp += xp;
    entry.level = resolveArmoryLevel(entry.xp, entry.level);
  }

  // ──────────────────────────────────────────────────────────────────────
  // Hero Quests (Phase 2) — repeatable per-hero quests that pay Armory XP
  // ──────────────────────────────────────────────────────────────────────

  /** Bronze / Silver / Gold targets per quest kind. Numbers tuned against
   *  actual run data so a Bronze tier represents ~2-3 dedicated runs of
   *  focused play; Silver ~10-15; Gold a long grind. */
  static HERO_QUEST_TARGETS: Record<'kills' | 'damage' | 'stage', Record<'bronze' | 'silver' | 'gold', number>> = {
    kills:  { bronze: 5_000,       silver: 25_000,      gold: 100_000 },
    damage: { bronze: 50_000_000,  silver: 500_000_000, gold: 5_000_000_000 },
    stage:  { bronze: 20,          silver: 40,          gold: 80 },
  };
  /** Tier rewards (Hero Armory XP). Bronze 30 ≈ 1/3 of a fresh-hero level;
   *  Gold 300 ≈ 3 levels for a fresh hero, fractional later. Modest by
   *  design — quests are bonus XP, not a replacement for run-share path. */
  static HERO_QUEST_REWARDS: Record<'bronze' | 'silver' | 'gold', number> = {
    bronze: 30, silver: 100, gold: 300,
  };
  /** Loops at the active tier required to unlock the next tier. */
  static HERO_QUEST_LOOPS_TO_PROMOTE = 5;

  private ensureHeroStats(heroKey: string): HeroLifetimeStats {
    if (!this.account.heroStats) this.account.heroStats = {};
    let s = this.account.heroStats[heroKey];
    if (!s) {
      s = { kills: 0, damageDealt: 0, bossesKilled: 0, highestStage: 0 };
      this.account.heroStats[heroKey] = s;
    }
    return s;
  }

  /** Per-hero kill counter (called from killEnemy with the killer's heroKey). */
  trackHeroKill(heroKey: string, isBoss: boolean): void {
    const s = this.ensureHeroStats(heroKey);
    s.kills++;
    if (isBoss) s.bossesKilled++;
  }
  /** Per-hero damage counter — small numbers add up; clamp to non-negative. */
  trackHeroDamage(heroKey: string, amount: number): void {
    if (amount <= 0) return;
    this.ensureHeroStats(heroKey).damageDealt += amount;
  }
  /** Per-hero peak stage — set whenever this hero is the deployed main. */
  trackHeroStage(heroKey: string, stage: number): void {
    const s = this.ensureHeroStats(heroKey);
    if (stage > s.highestStage) s.highestStage = stage;
  }

  /** Get-or-init the (heroKey, kind) progress entry. Snapshots the current
   *  lifetime counter so progress reads as "delta since this loop started"
   *  for repeatable kills/damage tracking. Stage doesn't use snapshotting
   *  since it's already a peak value. */
  getHeroQuestProgress(heroKey: string, kind: 'kills' | 'damage' | 'stage'): HeroQuestProgress {
    if (!this.account.heroQuestProgress) this.account.heroQuestProgress = {};
    const key = `${heroKey}_${kind}`;
    let p = this.account.heroQuestProgress[key];
    if (!p) {
      const stats = this.ensureHeroStats(heroKey);
      const snapshot = kind === 'kills' ? stats.kills : kind === 'damage' ? stats.damageDealt : 0;
      p = { heroKey, kind, currentTier: 'bronze', tierCompletions: 0, snapshotAtLoopStart: snapshot };
      this.account.heroQuestProgress[key] = p;
    }
    return p;
  }

  /** Current progress for a hero quest. For kills/damage this is the delta
   *  from the loop snapshot; for stage it's the lifetime peak. Capped at
   *  the active-tier target so the bar never overshoots. */
  getHeroQuestCurrent(heroKey: string, kind: 'kills' | 'damage' | 'stage'): number {
    const stats = this.ensureHeroStats(heroKey);
    const prog = this.getHeroQuestProgress(heroKey, kind);
    if (kind === 'stage') return Math.min(stats.highestStage, GameStateManager.HERO_QUEST_TARGETS.stage[prog.currentTier]);
    const lifetime = kind === 'kills' ? stats.kills : stats.damageDealt;
    const delta = Math.max(0, lifetime - prog.snapshotAtLoopStart);
    return Math.min(delta, GameStateManager.HERO_QUEST_TARGETS[kind][prog.currentTier]);
  }
  getHeroQuestTarget(heroKey: string, kind: 'kills' | 'damage' | 'stage'): number {
    const prog = this.getHeroQuestProgress(heroKey, kind);
    return GameStateManager.HERO_QUEST_TARGETS[kind][prog.currentTier];
  }
  getHeroQuestReward(heroKey: string, kind: 'kills' | 'damage' | 'stage'): number {
    const prog = this.getHeroQuestProgress(heroKey, kind);
    return GameStateManager.HERO_QUEST_REWARDS[prog.currentTier];
  }
  isHeroQuestCompleted(heroKey: string, kind: 'kills' | 'damage' | 'stage'): boolean {
    return this.getHeroQuestCurrent(heroKey, kind) >= this.getHeroQuestTarget(heroKey, kind);
  }

  // ──────────────────────────────────────────────────────────────────────
  // Mastery Challenges (Phase 4) — one-time, account-wide feats
  // ──────────────────────────────────────────────────────────────────────

  isMasteryClaimed(id: string): boolean {
    return !!(this.account.masteryClaimed?.[id]);
  }

  /** Mark a mastery as claimed and grant its rune reward. Idempotent — a
   *  second call for the same id returns 0. Caller persists via save(). */
  claimMastery(id: string): number {
    const def = getMasteryChallenge(id);
    if (!def) return 0;
    if (!this.account.masteryClaimed) this.account.masteryClaimed = {};
    if (this.account.masteryClaimed[id]) return 0;
    this.account.masteryClaimed[id] = true;
    this.player.resources.loot += def.runes;
    return def.runes;
  }

  /** Evaluate fast-zone challenges given the seconds-to-clear value just
   *  recorded. Auto-claims any unclaimed `fast_zone` challenge whose target
   *  is ≥ the time AND whose `requiresStage` gate (if any) is satisfied by
   *  the current `player.currentLevel`. Updates the personal-best tracker.
   *  Returns the list of challenges that were just claimed (BattleScene
   *  shows a toast for each). */
  evaluateFastZoneClear(seconds: number): MasteryChallenge[] {
    if (!this.account.masteryBestZoneTime || seconds < this.account.masteryBestZoneTime) {
      this.account.masteryBestZoneTime = seconds;
    }
    const stage = this.player.currentLevel;
    const claimed: MasteryChallenge[] = [];
    for (const c of MASTERY_CHALLENGES) {
      if (c.kind !== 'fast_zone') continue;
      if (this.isMasteryClaimed(c.id)) continue;
      if (c.requiresStage !== undefined && stage < c.requiresStage) continue;
      if (seconds <= c.target) {
        this.claimMastery(c.id);
        claimed.push(c);
      }
    }
    return claimed;
  }

  /** Evaluate stage-gate challenges (high_stage / no_death_to_stage /
   *  no_potion_to_stage / solo_to_stage). Called every time stage advances.
   *  Returns claimed challenges for toast. */
  evaluateStageGate(stage: number): MasteryChallenge[] {
    const claimed: MasteryChallenge[] = [];
    const deaths = this.player.runDeaths ?? 0;
    const potions = this.player.runPotionsUsed ?? 0;
    const maxHeroes = this.player.runMaxHeroesDeployed ?? 1;
    for (const c of MASTERY_CHALLENGES) {
      if (this.isMasteryClaimed(c.id)) continue;
      let qualifies = false;
      if (c.kind === 'high_stage') qualifies = stage >= c.target;
      else if (c.kind === 'no_death_to_stage')  qualifies = stage >= c.target && deaths === 0;
      else if (c.kind === 'no_potion_to_stage') qualifies = stage >= c.target && potions === 0;
      else if (c.kind === 'solo_to_stage')      qualifies = stage >= c.target && maxHeroes <= 1;
      if (qualifies) {
        this.claimMastery(c.id);
        claimed.push(c);
      }
    }
    return claimed;
  }

  evaluateBossesInRun(): MasteryChallenge[] {
    const bosses = this.player.runBossesKilled ?? 0;
    const claimed: MasteryChallenge[] = [];
    for (const c of MASTERY_CHALLENGES) {
      if (c.kind !== 'bosses_in_run') continue;
      if (this.isMasteryClaimed(c.id)) continue;
      if (bosses >= c.target) {
        this.claimMastery(c.id);
        claimed.push(c);
      }
    }
    return claimed;
  }

  evaluateInfernoZone(zone: number): MasteryChallenge[] {
    const claimed: MasteryChallenge[] = [];
    for (const c of MASTERY_CHALLENGES) {
      if (c.kind !== 'inferno_zone') continue;
      if (this.isMasteryClaimed(c.id)) continue;
      if (zone >= c.target) {
        this.claimMastery(c.id);
        claimed.push(c);
      }
    }
    return claimed;
  }

  // ──────────────────────────────────────────────────────────────────────

  /** Claim a completed hero quest. Resets the loop snapshot, bumps
   *  `tierCompletions`, auto-promotes Bronze → Silver → Gold every 5
   *  completions, deposits the tier reward as direct Armory XP. Returns
   *  the XP granted (0 if the quest isn't actually complete yet). */
  claimHeroQuest(heroKey: string, kind: 'kills' | 'damage' | 'stage'): number {
    const prog = this.getHeroQuestProgress(heroKey, kind);
    const target = this.getHeroQuestTarget(heroKey, kind);
    const current = this.getHeroQuestCurrent(heroKey, kind);
    if (current < target) return 0;

    const reward = this.getHeroQuestReward(heroKey, kind);
    this.grantDirectArmoryXp(heroKey, reward);

    prog.tierCompletions++;
    // Re-snapshot so the next loop counts from now.
    if (kind !== 'stage') {
      const stats = this.ensureHeroStats(heroKey);
      prog.snapshotAtLoopStart = kind === 'kills' ? stats.kills : stats.damageDealt;
    }
    // Auto-promote tier after N completions.
    if (prog.tierCompletions >= GameStateManager.HERO_QUEST_LOOPS_TO_PROMOTE) {
      if (prog.currentTier === 'bronze') { prog.currentTier = 'silver'; prog.tierCompletions = 0; }
      else if (prog.currentTier === 'silver') { prog.currentTier = 'gold'; prog.tierCompletions = 0; }
      // Gold loops forever — no cap on Gold tier completions.
    }
    return reward;
  }

  /** Equip `passiveId` into slot `slotIdx` (0/1/2) for this heroKey. Validates:
   *  slot is unlocked, passive is compatible with the hero, not already equipped
   *  in another slot. Returns true if the change was applied. */
  equipArmoryPassive(hero: Hero, slotIdx: 0 | 1 | 2, passiveId: string): boolean {
    const key = this.getArmoryKey(hero);
    const entry = this.ensureArmoryEntry(key);
    if (slotIdx >= unlockedPassiveSlots(entry.level)) return false;
    const def = getPassiveDef(passiveId);
    if (!def || def.heroClass !== hero.heroClass) return false;
    if (def.heroKey && def.heroKey !== (hero.heroKey ?? hero.heroClass)) return false;
    // No dupes across slots.
    const ids = [...entry.passiveIds];
    while (ids.length < ARMORY_MAX_PASSIVES) ids.push('');
    for (let i = 0; i < ids.length; i++) {
      if (i !== slotIdx && ids[i] === passiveId) return false;
    }
    ids[slotIdx] = passiveId;
    entry.passiveIds = ids.filter((x) => x.length > 0);
    return true;
  }

  /** Remove the passive in a single slot without charging runes. Normal flow
   *  for respec is {@link respecArmoryPassives}; this is a building block. */
  unequipArmoryPassive(hero: Hero, slotIdx: 0 | 1 | 2): boolean {
    const key = this.getArmoryKey(hero);
    const entry = this.ensureArmoryEntry(key);
    if (slotIdx >= entry.passiveIds.length) return false;
    entry.passiveIds = entry.passiveIds.filter((_, i) => i !== slotIdx);
    return true;
  }

  /** Wipe all passive picks for this heroKey, charging {@link ARMORY_RESPEC_COST_RUNES}.
   *  Returns true iff the player had enough runes and at least one pick existed. */
  respecArmoryPassives(hero: Hero): boolean {
    const key = this.getArmoryKey(hero);
    const entry = this.ensureArmoryEntry(key);
    if (entry.passiveIds.length === 0) return false;
    if (ARMORY_RESPEC_COST_RUNES > 0) {
      const runes = this.player.resources.loot ?? 0;
      if (runes < ARMORY_RESPEC_COST_RUNES) return false;
      this.player.resources.loot = runes - ARMORY_RESPEC_COST_RUNES;
      this.trackMaterialsSpent(ARMORY_RESPEC_COST_RUNES);
    }
    entry.passiveIds = [];
    return true;
  }

  /** List of passives available for this hero's class/heroKey. UI feed. */
  listArmoryPassives(hero: Hero): PassiveDef[] {
    return getPassivesForHero(hero.heroClass, hero.heroKey);
  }

  /** Run-start hook — copies each deployed hero's master passive list into
   *  the hero's run-scoped `activePassiveIds` snapshot so respecs mid-run
   *  don't retro-apply. Call once when a run begins. */
  snapshotActiveArmoryPassives(): void {
    for (const idx of this.player.deployedHeroes) {
      const hero = this.player.heroes[idx];
      if (!hero) continue;
      const key = this.getArmoryKey(hero);
      const entry = this.account.armoryByHero?.[key];
      hero.activePassiveIds = entry ? [...entry.passiveIds] : [];
    }
  }

  private _generateMerchantConsumables(): void {
    const pool = CONSUMABLE_DEFS.filter((c) => c.id !== 'mimic_chest');
    const items: MerchantItem[] = [];
    for (let i = 0; i < MERCHANT_INVENTORY_SIZE && pool.length > 0; i++) {
      const idx = Math.floor(Math.random() * pool.length);
      items.push({ consumable: pool[idx], purchased: false });
      pool.splice(idx, 1);
    }
    this.player.merchant.items = items;
  }

  private _generateMerchantEquipment(): void {
    const lockedSlots = new Set(this.player.merchant.lockedEquipmentSlots ?? []);
    const prevEquip = this.player.merchant.equipmentItems ?? [];
    const itemLevel = this.player.currentLevel;
    const equipItems: MerchantEquipmentItem[] = [];
    for (let i = 0; i < 3; i++) {
      if (lockedSlots.has(i) && prevEquip[i] && !prevEquip[i].purchased) {
        equipItems.push(prevEquip[i]);
        continue;
      }
      const lootTier = 2 + Math.floor(Math.random() * 4);
      // Merchant floors rarity at uncommon — pass as minRarity so the rolled rarity
      // clamps up and all derived fields (name prefix, stats, effect) key off the
      // final tier. Post-hoc `.rarity = 'uncommon'` left common stats stamped
      // with an uncommon label.
      const equip = generateEquipment(itemLevel, lootTier, 'boss', 'uncommon');
      const PRICE_RARITY: Record<string, number> = { rare: 1.0, epic: 3.2, legendary: 12.5, unique: 25 };
      const rarityMult = PRICE_RARITY[equip.rarity] ?? 1.0;
      const levelBase = Math.floor(400 * Math.pow(itemLevel, 1.5));
      const relicMult = equip.slot.startsWith('relic') ? 3 : 1;
      // Per user feedback: vendor relic/armor/weapon prices were too high at
      // mid-stage (≈70k gold for a rare relic at stage 22). Divide by an extra
      // 2.5× — the formula now reads `/ 7.5` in total (was `/ 3`) so the same
      // stage-22 rare relic lands ~26-28k gold instead. Mystery items keep
      // their own pricing via `getMysteryItemPrice()` and are unaffected.
      const goldPrice = Math.floor(levelBase * rarityMult * (1.4 + Math.random() * 0.4) / 7.5 * relicMult);
      equipItems.push({ equipment: equip, goldPrice, purchased: false });
    }
    this.player.merchant.equipmentItems = equipItems;
  }

  /** Purchase a merchant item by index */
  purchaseMerchantItem(index: number): boolean {
    const item = this.player.merchant.items[index];
    if (!item || item.purchased) return false;

    const def = item.consumable;
    const price = item.priceOverride ?? def.price;
    if (def.currency === 'gold') {
      if (this.player.resources.gold < price) return false;
      this.player.resources.gold -= price;
      this.trackGoldSpent(price);
    } else {
      if (this.player.resources.loot < price) return false;
      this.player.resources.loot -= price;
      this.trackMaterialsSpent(price);
    }

    item.purchased = true;
    this.trackConsumableBought();
    this.checkAchievements();

    // mimic_chest is special — activate immediately (handled by BattleScene)
    if (def.id === 'mimic_chest') {
      this.player.activeConsumables.push({ id: def.id, remainingWaves: def.durationWaves });
      return true;
    }

    // All other consumables go to the potion bar; fall back to immediate activation if bar is full
    if (!this.addToPotionBar(def.id)) {
      const existing = this.player.activeConsumables.find((c) => c.id === def.id);
      if (existing) existing.remainingWaves += def.durationWaves;
      else this.player.activeConsumables.push({ id: def.id, remainingWaves: def.durationWaves });
    }

    return true;
  }

  /** Add a consumable to the potion bar stash (stacks same type). Returns false
   *  if the bar is full OR the stack is at its cap. Vitality and Mana cap at
   *  5 to prevent burst-tap stockpiling; everything else stacks unlimited. */
  static readonly POTION_STACK_CAP = 5;
  static readonly STACK_CAPPED_POTIONS: ReadonlySet<string> = new Set(['potion_vitality', 'potion_mana']);
  addToPotionBar(id: string): boolean {
    if (!this.player.potionBar) this.player.potionBar = [];
    // Stack onto an existing slot with the same id (even if currently empty)
    const existing = this.player.potionBar.find((s) => s.id === id);
    if (existing) {
      if (GameStateManager.STACK_CAPPED_POTIONS.has(id)
        && existing.count >= GameStateManager.POTION_STACK_CAP) {
        return false; // at the cap — drop the pickup
      }
      existing.count++; return true;
    }
    // Reuse a currently-empty slot (count=0) so positions stay stable
    const emptySlot = this.player.potionBar.find((s) => s.count <= 0);
    if (emptySlot) { emptySlot.id = id; emptySlot.count = 1; return true; }
    // No empty slot — check against active (non-empty) slot count
    const maxSlots = this.getPotionBarMaxSlots();
    if (this.player.potionBar.filter((s) => s.count > 0).length >= maxSlots) return false;
    this.player.potionBar.push({ id, count: 1 });
    return true;
  }

  /** Use one potion from a bar slot index. Returns the ConsumableId used, or null. */
  usePotionFromBar(slotIdx: number): ConsumableId | null {
    if (!this.player.potionBar) return null;
    const slot = this.player.potionBar[slotIdx];
    if (!slot || slot.count <= 0) return null;
    const id = slot.id as ConsumableId;
    slot.count--;
    // Keep slot in array at its position (don't splice) so other slots don't shift

    // Buff-type consumables (duration > 1 wave): add to active buffs so combat picks them up
    const def = CONSUMABLE_DEFS.find((c) => c.id === id);
    if (def && def.durationWaves > 1) {
      if (!this.player.activeConsumables) this.player.activeConsumables = [];
      const existing = this.player.activeConsumables.find((c) => c.id === id);
      if (existing) existing.remainingWaves += def.durationWaves;
      else this.player.activeConsumables.push({ id, remainingWaves: def.durationWaves });
    }
    return id;
  }

  /** Max slots in the potion bar (base 2, +1 per extra_potion_slots passive node). */
  getPotionBarMaxSlots(): number {
    const base = this.player.potionBarSlots ?? 2;
    const extra = getPassiveBonuses(this.player.passiveTree ?? {}).extraPotionSlots;
    return base + Math.round(extra);
  }

  /** Tick down consumable wave counters (call at wave start) */
  tickConsumableWaves(): void {
    if (!this.player.activeConsumables) this.player.activeConsumables = [];
    this.player.activeConsumables = this.player.activeConsumables.filter((c) => {
      c.remainingWaves--;
      return c.remainingWaves > 0;
    });
  }

  /** Check if a consumable buff is active */
  hasActiveConsumable(id: ConsumableId): boolean {
    if (!this.player.activeConsumables) return false;
    return this.player.activeConsumables.some((c) => c.id === id);
  }

  /** Remove one instance of an active consumable (used for one-shot buffs like resurrection) */
  removeActiveConsumable(id: ConsumableId): void {
    if (!this.player.activeConsumables) return;
    const idx = this.player.activeConsumables.findIndex((c) => c.id === id);
    if (idx >= 0) this.player.activeConsumables.splice(idx, 1);
  }

  /** Purchase equipment from merchant by index */
  /** Merchant price multiplier — Merchant's Coin sigil makes everything 15% pricier. */
  getMerchantPriceMult(): number {
    return this.hasEquippedSigilSpecial('merchants_coin') ? 1.15 : 1.0;
  }

  /** Final cost of a merchant equipment slot after sigil multipliers. */
  getMerchantEquipmentCost(index: number): number {
    const eqItems = this.player.merchant.equipmentItems;
    if (!eqItems || index < 0 || index >= eqItems.length) return 0;
    return Math.ceil(eqItems[index].goldPrice * this.getMerchantPriceMult());
  }

  purchaseMerchantEquipment(index: number): boolean {
    const eqItems = this.player.merchant.equipmentItems;
    if (!eqItems || index < 0 || index >= eqItems.length) return false;
    const item = eqItems[index];
    if (item.purchased) return false;
    const cost = this.getMerchantEquipmentCost(index);
    if (this.player.resources.gold < cost) return false;
    this.player.resources.gold -= cost;
    this.trackGoldSpent(cost);
    this.checkAchievements();
    item.purchased = true;
    this.player.inventory.push(item.equipment);
    this.player.newLootCount = (this.player.newLootCount || 0) + 1;
    return true;
  }

  // ---------- Achievement Tracking ----------

  /** Track a crit hit — bumps lifetime stat AND any active "Critical
   *  Striker"-kind daily quests via `trackCritHit`. */
  trackCrit(): void {
    this.account.lifetimeStats.totalCrits++;
    this.trackCritHit();
  }

  /** Track an ability use */
  trackAbilityUse(): void { this.account.lifetimeStats.totalAbilitiesUsed++; }

  /** Track a death */
  trackDeath(): void { this.account.lifetimeStats.totalDeaths++; }

  /** Track a new run started */
  trackRunStart(): void { this.account.lifetimeStats.totalRunsStarted++; }

  /** Track gold earned */
  trackGoldEarned(amount: number): void { this.account.lifetimeStats.totalGoldEarned += amount; }

  /** Track gold spent */
  trackGoldSpent(amount: number): void { this.account.lifetimeStats.totalGoldSpent += amount; }

  /** Track loot spent */
  trackMaterialsSpent(amount: number): void { this.account.lifetimeStats.totalMaterialsSpent += amount; }

  /** Track item found (with rarity) */
  trackItemFound(rarity: string): void {
    this.account.lifetimeStats.totalItemsFound++;
    if (rarity === 'epic') this.account.lifetimeStats.epicItemsFound++;
    if (rarity === 'legendary') this.account.lifetimeStats.legendaryItemsFound++;
  }

  /** Track item sold */
  trackItemSold(): void { this.account.lifetimeStats.itemsSold++; }

  /** Track consumable bought */
  trackConsumableBought(): void { this.account.lifetimeStats.consumablesBought++; }

  /** Track hero class used */
  trackClassUsed(heroClass: HeroClass): void {
    if (!this.account.lifetimeStats.classesUsed.includes(heroClass)) {
      this.account.lifetimeStats.classesUsed.push(heroClass);
    }
  }

  /** Track hero level */
  trackHeroLevel(level: number): void {
    if (level > this.account.lifetimeStats.highestHeroLevel) {
      this.account.lifetimeStats.highestHeroLevel = level;
    }
  }

  /** Track wave reached */
  trackHighestWave(totalWave: number): void {
    if (totalWave > this.account.lifetimeStats.highestWaveReached) {
      this.account.lifetimeStats.highestWaveReached = totalWave;
    }
  }

  /** Track run score */
  trackRunScore(score: number): void {
    if (score > this.account.lifetimeStats.bestRunScore) {
      this.account.lifetimeStats.bestRunScore = score;
    }
  }

  /** Track tome maxed */
  trackTomeMaxed(): void { this.account.lifetimeStats.tomesMaxed++; }

  /** Get the current stat value for a given achievement chain */
  private getChainStatValue(chainId: string): number {
    const ls = this.account.lifetimeStats;
    switch (chainId) {
      case 'zone': return this.player.highestLevel;
      case 'gold_earned': return ls.totalGoldEarned;
      case 'score': return ls.bestRunScore;
      case 'deaths': return ls.totalDeaths;
      case 'runs': return ls.totalRunsStarted;
      case 'slayer': return ls.totalKillsAllTime;
      case 'boss': return ls.totalBossKills;
      case 'speed': return ls.fastBossKills;
      case 'crits': return ls.totalCrits;
      case 'abilities': return ls.totalAbilitiesUsed;
      case 'beast': return ls.beastKills;
      case 'undead': return ls.undeadKills;
      case 'orc': return ls.orcKills;
      case 'demon': return ls.demonKills;
      case 'dragon': return ls.dragonKills;
      case 'hero_level': return ls.highestHeroLevel;
      case 'squad': return this.player.deployedHeroes.length;
      case 'classes': return ls.classesUsed.length;
      case 'loot': return ls.totalItemsFound;
      case 'epic': return ls.epicItemsFound;
      case 'legendary_items': return ls.legendaryItemsFound;
      case 'sell': return ls.itemsSold;
      case 'spend': return ls.totalGoldSpent;
      case 'potions': return ls.consumablesBought;
      case 'tomes': return ls.tomesMaxed;
      default: return 0;
    }
  }

  /** Check all achievements against current lifetime stats and update progress.
   *  Handles tiered chains: only the current active tier per chain is checked.
   *  Returns list of newly completed achievement IDs. */
  checkAchievements(): string[] {
    const newlyCompleted: string[] = [];

    for (const ach of this.account.achievements) {
      if (ach.completed) continue;
      const def = ACHIEVEMENTS.find((a) => a.id === ach.id);
      if (!def) continue;

      // Check if previous tier in this chain is completed (tier gating)
      if (def.tier > 1) {
        const prevTier = ACHIEVEMENTS.find((a) => a.chain === def.chain && a.tier === def.tier - 1);
        if (prevTier) {
          const prevProgress = this.account.achievements.find((a) => a.id === prevTier.id);
          if (!prevProgress || !prevProgress.completed) continue; // previous tier not done yet
        }
      }

      const current = this.getChainStatValue(def.chain);
      ach.current = current;
      if (current >= def.target) {
        ach.completed = true;
        newlyCompleted.push(ach.id);
      }
    }
    if (newlyCompleted.length > 0) {
      this.saveAccount();
      // Phase 3b: mirror unlock to Steam overlay popup. No-op outside Electron
      // (web build) and for internal ids not yet mapped to a Steam API name
      // (achievementMap.ts is empty until Steamworks Direct registration).
      // Lazy-imported so the web bundle can tree-shake the steam/ subdirectory
      // (currently nothing else depends on it).
      void import('../steam/achievementMap.js').then(({ fireSteamAchievement }) => {
        for (const id of newlyCompleted) fireSteamAchievement(id);
      }).catch(() => { /* steam mirror is best-effort; swallow load failures */ });
    }
    return newlyCompleted;
  }

  /** Claim an achievement reward. Returns true if successful. */
  claimAchievement(achievementId: string): boolean {
    const ach = this.account.achievements.find((a) => a.id === achievementId);
    if (!ach || !ach.completed || ach.claimed) return false;
    const def = ACHIEVEMENTS.find((a) => a.id === achievementId);
    if (!def) return false;
    this.player.resources.gold += def.rewards.gold;
    this.player.resources.loot += def.rewards.loot;
    ach.claimed = true;
    this.saveAccount();
    return true;
  }

  /** Get count of completed but unclaimed achievements */
  getUnclaimedAchievementCount(): number {
    return this.account.achievements.filter((a) => {
      if (!a.completed || a.claimed) return false;
      const def = ACHIEVEMENTS.find((d) => d.id === a.id);
      return def && (def.rewards.loot > 0 || def.rewards.gold > 0);
    }).length;
  }

  /** Get the active (current) tier for each achievement chain.
   *  Returns the first uncompleted tier, or the last completed tier if all done. */
  getActiveAchievements(): { def: typeof ACHIEVEMENTS[0]; progress: AchievementProgress }[] {
    const seen = new Set<string>();
    const result: { def: typeof ACHIEVEMENTS[0]; progress: AchievementProgress }[] = [];

    // Sort by chain then tier to process in order
    const sorted = [...ACHIEVEMENTS].sort((a, b) => {
      if (a.chain !== b.chain) return a.chain.localeCompare(b.chain);
      return a.tier - b.tier;
    });

    for (const def of sorted) {
      if (seen.has(def.chain)) continue;
      const progress = this.account.achievements.find((a) => a.id === def.id);
      if (!progress) continue;

      if (!progress.completed) {
        // This is the active tier (first uncompleted)
        // But check tier gating: previous tier must be completed
        if (def.tier > 1) {
          const prevTier = ACHIEVEMENTS.find((a) => a.chain === def.chain && a.tier === def.tier - 1);
          if (prevTier) {
            const prevProgress = this.account.achievements.find((a) => a.id === prevTier.id);
            if (!prevProgress || !prevProgress.completed) continue; // locked
          }
        }
        seen.add(def.chain);
        result.push({ def, progress });
      } else {
        // Completed — check if there's a next tier
        const nextTier = ACHIEVEMENTS.find((a) => a.chain === def.chain && a.tier === def.tier + 1);
        if (nextTier) {
          // Don't mark chain as seen yet; we want the next uncompleted tier
          continue;
        } else {
          // All tiers completed — show the last one
          seen.add(def.chain);
          result.push({ def, progress });
        }
      }
    }
    return result;
  }

}

